const libFableServiceProviderBase = require('fable-serviceproviderbase');
// The Pict-Input-Templated extension provider — fills a Templated cell's per-row content.
// pict-section-form wires it from metatemplate `DefaultInputExtensions` at load; our runtime
// tabular shim has to register + wire it by hand (see _installDocumentLinkSupport).
const libPictInputTemplated = require('pict-section-form/source/providers/inputs/Pict-Provider-Input-Templated.js');

/**
 * Row-aware tabular Templated input. The stock pict-section-form Pict-Input-Templated is
 * broken for tabular cells: `_handleInitialize()` calls `handleContentUpdate()` WITHOUT the
 * row index, so it renders the cell's `PictForm.Template` against FORM-level data
 * (`getMarshalDestinationObject()`) instead of the per-row record — every Templated cell in a
 * recordset LIST row paints empty (the literal string "undefined"). This subclass overrides
 * the tabular marshal to render the cell template against the actual ROW record, so
 * `{~D:Record.IDDocumentCount~}`, `Record.BucketDate`, `Record.BucketLabel` resolve — which is
 * what the per-day Documents button needs. Registered + wired in `_installDocumentLinkSupport`.
 */
class WalbecTabularTemplatedInput extends libPictInputTemplated
{
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		try
		{
			if (pInput && pInput.PictForm && (typeof pInput.PictForm.Template === 'string') && (pRowIndex != null))
			{
				const tmpMarshalDestination = pView.getMarshalDestinationObject() || {};
				// The tabular group binds to this address (e.g. WalbecDailyRecords). Fall back to
				// the known per-day records if the group didn't carry it.
				const tmpRecordSetAddress = (pGroup && pGroup.RecordSetAddress) || 'WalbecDailyRecords';
				const tmpRecords = Array.isArray(tmpMarshalDestination[tmpRecordSetAddress]) ? tmpMarshalDestination[tmpRecordSetAddress] : null;
				const tmpRow = tmpRecords ? tmpRecords[pRowIndex] : null;
				if (tmpRow)
				{
					// Render the cell template against the ROW (so `Record.*` is the row's data).
					const tmpContent = this.pict.parseTemplate(pInput.PictForm.Template, tmpRow, null, [ this ], this);
					this.pict.ContentAssignment.assignContent(this.getTabularContentDisplayInputID(pInput.Macro.RawHTMLID, pRowIndex), tmpContent);
					return true;
				}
			}
		}
		catch (pError)
		{
			this.pict.log.warn(`WalbecTabularTemplatedInput tabular render error: ${pError.message || pError}`);
		}
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}
}

/**
 * Dashboard screen styling. Every rule is scoped to `[id^="PSRS-Dashboard-"]`, so it is safe inside any host page;
 * the page-level rules (print isolation, gutters, the bottom toolbar) stay with the data app's own chrome.
 */
const _SCOPED_DASHBOARD_CSS = /*css*/`
	/* ---------------------------------------------------------------
	   FULL-WIDTH DASHBOARDS
	   ---------------------------------------------------------------
	   pict-section-form puts Bulma's .container on every form section,
	   which caps it at 960px (and ~1152/1344px at wider breakpoints).
	   That measure is right for a FORM -- a readable line length -- and
	   wrong for a dashboard, where the content is a 48-column gradation
	   table and a wide control chart. On a 1168px window the table got
	   960px and the user scrolled horizontally for 208px they already
	   had; on a 2560px monitor the cap throws away over 1100px.

	   Scoped to [id^="PSRS-Dashboard-"] so ordinary forms keep the
	   readable measure the container exists to give them. */
	[id^="PSRS-Dashboard-"] .pict-form-section.container
	{
		max-width: none;
		width: 100%;
		margin-left: 0;
		margin-right: 0;
	}

	[id^="PSRS-Dashboard-"] .section-table-wrapper td > span:first-child { display: none; }
	[id^="PSRS-Dashboard-"] .section-table-wrapper td { white-space: nowrap; }
	/* Section / sub-section headers — turn the muted form labels
	   into proper dashboard section titles. */
	[id^="PSRS-Dashboard-"] h2.section-header,
	[id^="PSRS-Dashboard-"] h3.section-subheader
	{
		font-size: 1rem;
		font-weight: 700;
		color: #1a202c;
		letter-spacing: -0.005em;
		margin: 1.4rem 0 0.6rem;
		padding-bottom: 0.4rem;
		border-bottom: 2px solid #e6e8ee;
		text-transform: none;
	}
	[id^="PSRS-Dashboard-"] h2.section-header.is-hidden,
	[id^="PSRS-Dashboard-"] h3.section-subheader.is-hidden
	{
		display: none;
	}
	/* KPI tile cards — wrap each ReadOnly form input in a polished
	   card with label on top, large bold value below. We use the
	   :has() selector to scope the treatment to columns that
	   actually contain a readonly input (the KPI cells); chart /
	   tabular sections are unaffected. */
	[id^="PSRS-Dashboard-"] .column:has(> .field > .control > .input[readonly]),
	[id^="PSRS-Dashboard-"] .pict-form-vertical-input.column:has(.input[readonly])
	{
		background: #ffffff;
		border: 1px solid #e6e8ee;
		border-radius: 12px;
		padding: 0.85rem 1rem 0.95rem;
		box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
		margin: 0.4rem;
		transition: box-shadow 0.15s ease;
	}
	[id^="PSRS-Dashboard-"] .column:has(> .field > .control > .input[readonly]):hover,
	[id^="PSRS-Dashboard-"] .pict-form-vertical-input.column:has(.input[readonly]):hover
	{
		box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
	}
	[id^="PSRS-Dashboard-"] .column:has(> .field > .control > .input[readonly]) > label,
	[id^="PSRS-Dashboard-"] .pict-form-vertical-input.column:has(.input[readonly]) label
	{
		color: #64748b;
		font-size: 0.72rem;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		font-weight: 600;
		margin-bottom: 0.25rem;
		display: block;
	}
	/* The readonly input itself loses its border / background and
	   becomes the headline number for the card. SCOPED to the KPI
	   card columns (same :has() scope as the card treatment above) so
	   it does NOT leak onto tabular readonly cells — those are styled
	   separately below. */
	[id^="PSRS-Dashboard-"] .column:has(> .field > .control > .input[readonly]) .input[readonly],
	[id^="PSRS-Dashboard-"] .pict-form-vertical-input.column:has(.input[readonly]) .input[readonly]
	{
		border: none !important;
		background: transparent !important;
		box-shadow: none !important;
		padding: 0 !important;
		font-weight: 700;
		font-size: 1.6rem;
		color: #1a202c;
		height: auto;
		text-align: left;
	}
	/* Tabular readonly value cells (PreciseNumberReadOnly etc. inside a
	   recordset table) — borderless like the KPI value but at the
	   table's own scale, and content-sized so formatted currency
	   ($9,791,990.30) is never clipped. NOT the 1.6rem KPI headline. */
	[id^="PSRS-Dashboard-"] table input[readonly][id^="TABULAR-DATA-"]
	{
		border: none !important;
		background: transparent !important;
		box-shadow: none !important;
		padding: 0 0.35rem 0 0 !important;
		font-weight: 400;
		font-size: 0.9rem;
		color: #1a202c;
		height: auto !important;
		min-height: 0 !important;
		width: auto;
		min-width: 0;
		field-sizing: content;
		max-width: 14rem;
		text-align: right;
	}
	/* Strip the addons / icons the form template adds around inputs so
	   the value sits cleanly in the card. */
	[id^="PSRS-Dashboard-"] .column:has(.input[readonly]) .has-addons-right { justify-content: flex-start; }
	[id^="PSRS-Dashboard-"] .column:has(.input[readonly]) .control-status-icon,
	[id^="PSRS-Dashboard-"] .column:has(.input[readonly]) .help { display: none; }
`;

const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'DynamicDashboardLoader',
	// true: this loader owns the page (the data app) — bottom toolbar, router hooks, print isolation, global template
	// overrides. false: a host app embeds dashboards; only dashboard-scoped styling and registration are installed.
	AppChrome: true,
};

/**
 * @typedef {Object} DashboardHostOptions
 * @property {string} [ExtraFilter] - Meadow filter clauses ANDed onto every read the dashboard makes (project + role filters).
 * @property {string} [URLTemplate] - Where applying a filter navigates, when the host owns the route (receives
 *   `/FilterExperience/<serialized>` appended).
 * @property {boolean} [ShowDescription] - true shows the dashboard's Description under its title.
 */

/**
 * The "extra column" row template the framework renders inside the actions
 * <td> for every Document-backed recordset. Replaces the stock View link with
 * Open-in-new-tab + Download PDF buttons that route through
 * `pict.HeadlightDocumentRender`. `Record.Data.IDDocument` resolves per-row
 * inside the framework's tabular iteration.
 *
 * Template hash is per-recordset (see RecordsSet-MetaController#loadRecordSetConfiguration);
 * the inline `onclick` handlers survive re-renders without needing observers.
 */
function _walbecDocumentActionRowTemplate()
{
	return /*html*/`<td class="has-text-centered">
		<div class="buttons are-small is-centered">
			<button type="button" class="button is-link is-small"
				onclick="event.preventDefault(); _Pict.HeadlightDocumentRender.openDocument({~D:Record.Data.IDDocument~}, { target: '_blank' });"
				title="Open the rendered form in a new tab">
				<span class="icon"><i class="fa fa-up-right-from-square"></i></span>
				<span>Open</span>
			</button>
			<button type="button" class="button is-info is-small is-light"
				onclick="event.preventDefault(); _Pict.HeadlightDocumentRender.downloadPDF({~D:Record.Data.IDDocument~});"
				title="Download the rendered PDF">
				<span class="icon"><i class="fa fa-file-pdf"></i></span>
				<span>PDF</span>
			</button>
		</div>
	</td>`;
}

/**
 * The modal-body template for the per-day Documents drill-down: a table of the day's
 * Document records (one Walbec-DayDocuments-ModalRow per row), rendered into
 * pict-section-modal by `DynamicDashboardLoader.openDayDocuments`. Iterates
 * AppData.WalbecDayDocuments.Records.
 */
function _walbecDayDocumentsModalBody()
{
	// max-height + scroll keeps the popup "not too tall"; the modal width is widened in
	// openDayDocuments. Rendered ASYNC (parseTemplateByHash with a callback) so the
	// `{~E:Project^...~}` per-row project-name lookups resolve.
	return /*html*/`<div class="walbec-daydocs" style="max-height:60vh;overflow-y:auto;">
		<table class="table is-fullwidth is-striped is-narrow is-hoverable">
			<thead><tr>
				<th>Date</th><th>Project</th><th>Document</th><th class="has-text-right">&nbsp;</th>
			</tr></thead>
			<tbody>{~TS:Walbec-DayDocuments-ModalRow:AppData.WalbecDayDocuments.Records~}</tbody>
		</table>
	</div>`;
}

/**
 * One Document row inside the drill-down popup — Date (YYYY-MM-DD), Project name (resolved
 * from the Document's IDProject), Document title, and Open-in-tab + Download-PDF actions
 * routed through `pict.HeadlightDocumentRender`. `Record` is the fetched Document entity.
 */
function _walbecDayDocumentsModalRow()
{
	return /*html*/`<tr>
		<td style="white-space:nowrap;">{~DateFormat:Record.DocumentDate^YYYY-MM-DD~}</td>
		<td>{~E:Project^Record.IDProject^Walbec-DayDoc-ProjectName~}</td>
		<td>{~D:Record.Name~}</td>
		<td class="has-text-right" style="white-space:nowrap;">
			<div class="buttons are-small" style="flex-wrap:nowrap;justify-content:flex-end;margin-bottom:0;">
				<button type="button" class="button is-link is-small"
					onclick="event.preventDefault(); _Pict.HeadlightDocumentRender.openDocument({~D:Record.IDDocument~}, { target: '_blank' });"
					title="Open the rendered form in a new tab">
					<span class="icon"><i class="fa fa-up-right-from-square"></i></span><span>Open</span>
				</button>
				<button type="button" class="button is-info is-small is-light"
					onclick="event.preventDefault(); _Pict.HeadlightDocumentRender.downloadPDF({~D:Record.IDDocument~});"
					title="Download the rendered PDF">
					<span class="icon"><i class="fa fa-file-pdf"></i></span><span>PDF</span>
				</button>
			</div>
		</td>
	</tr>`;
}

/**
 * Renders a Project's name. Used via `{~E:Project^Record.IDProject^Walbec-DayDoc-ProjectName~}`
 * in the documents popup row — here `Record` is the fetched Project entity (cached by the
 * entity-render provider, so repeated IDProjects only fetch once).
 */
function _walbecDayDocProjectName()
{
	return /*html*/`{~D:Record.Name~}`;
}

/**
 * Loads per-customer dashboards from AppData + ManifestSchema and registers
 * them into the running pict-section-recordset instance at runtime.
 *
 * Storage contract (matches `headlight/web/data` plan, demo shortcut):
 *   - AppData (Type=AvailableDashboards, IDCustomer=N)        — list of dashboards to wire for this customer
 *   - ManifestSchema (Type=DataDashboard, Hash=H, IDCustomer) — the pict-section-recordset manifest for each dashboard
 *   - AppData (Type=AggregatedDashboardData, AppHash=H)       — bucketed records the recordset reads
 *
 * The loader is a no-op for customers without an AvailableDashboards record,
 * which preserves the existing behavior for customer 10 (LADOTD).
 */
class DynamicDashboardLoader extends libFableServiceProviderBase
{
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = Object.assign({}, _DEFAULT_CONFIGURATION, pOptions);
		super(pFable, tmpOptions, pServiceHash);
		/** @type {any} */
		this.pict = this.fable;
		/** @type {any} */
		this.log;
	}

	/**
	 * Runtime support for the dashboard Documents drill-down, installed once before the
	 * first dashboard renders:
	 *   1. pict-section-form (<= 1.1.8) ships tabular metatemplates for most input types
	 *      but NOT `Templated`, so a Templated cell inside a recordset LIST row throws
	 *      "missing input template" and takes the whole dashboard down. Until
	 *      pict-section-form publishes the pair, register it at runtime under every form
	 *      template-set prefix that already carries the tabular `Link` template
	 *      (Pict-MT-Base, the ReadOnly set, and the active theme sets — the dashboard form
	 *      runs under PictProvider-Theme-Edit). Mirrors the Begin/End pair added to
	 *      pict-section-form's Pict-DynamicTemplates-DefaultFormTemplates.js (publish later).
	 *   2. Register the two modal-content templates the Documents popup renders.
	 */
	_installDocumentLinkSupport()
	{
		if (this._documentLinkSupportInstalled)
		{
			return;
		}
		const tmpTemplateProvider = this.pict.TemplateProvider;
		if (!tmpTemplateProvider || !tmpTemplateProvider.templates)
		{
			return;
		}
		// 1. Tabular `Templated` metatemplate shim — register under every prefix that
		// already has the tabular `Link` template, so it lands in whatever set(s) the
		// dashboard form actually uses.
		const tmpReference = '-TabularTemplate-End-Input-InputType-Link';
		const tmpPrefixes = [ ...new Set(Object.keys(tmpTemplateProvider.templates)
			.filter((pHash) => pHash.endsWith(tmpReference))
			.map((pHash) => pHash.slice(0, -tmpReference.length))) ];
		const tmpBegin = /*html*/`<input type="hidden" {~D:Record.Macro.ControlAttr~} {~D:Record.Macro.HTMLName~} {~D:Record.Macro.InformaryTabular~} `;
		const tmpEnd = /*html*/` value=""><div id="DISPLAY-FOR-TABULAR-{~D:Record.Macro.RawHTMLID~}-{~D:Context[2].Key~}" class="pict-section-form-templated"></div>`;
		// A Templated cell only paints if an input extension is attached to its metatemplate
		// hash (its onDataMarshalToFormTabular fills the per-row DISPLAY-FOR-TABULAR div), and
		// the stock Pict-Input-Templated renders tabular cells against FORM-level data, not the
		// row — so it paints "undefined". We register the row-aware WalbecTabularTemplatedInput
		// and map each tabular Templated hash to it via DynamicInput.addDefaultInputProvider
		// (the same call the load-time `DefaultInputExtensions` processor makes, which a plain
		// addTemplate() can't carry). Without this the Documents button is a silent empty cell.
		if (!this.pict.providers['Walbec-Input-Templated'])
		{
			this.pict.addProvider('Walbec-Input-Templated', {}, WalbecTabularTemplatedInput);
		}
		const tmpDynamicInput = this.pict.providers.DynamicInput;
		let tmpInstalledCount = 0;
		for (const tmpPrefix of tmpPrefixes)
		{
			const tmpBeginHash = `${tmpPrefix}-TabularTemplate-Begin-Input-InputType-Templated`;
			const tmpEndHash = `${tmpPrefix}-TabularTemplate-End-Input-InputType-Templated`;
			if (!tmpTemplateProvider.templates[tmpBeginHash])
			{
				tmpTemplateProvider.addTemplate(tmpBeginHash, tmpBegin, 'DynamicDashboardLoader tabular Templated shim');
			}
			if (!tmpTemplateProvider.templates[tmpEndHash])
			{
				tmpTemplateProvider.addTemplate(tmpEndHash, tmpEnd, 'DynamicDashboardLoader tabular Templated shim');
				tmpInstalledCount++;
			}
			// getInputTemplateHash() resolves a tabular Templated cell to this Begin hash, so
			// this is the key DynamicInput looks up for the extension. Idempotent.
			if (tmpDynamicInput && typeof tmpDynamicInput.addDefaultInputProvider === 'function')
			{
				tmpDynamicInput.addDefaultInputProvider(tmpBeginHash, 'Walbec-Input-Templated');
			}
		}
		// 2. Documents popup content templates.
		tmpTemplateProvider.addTemplate('Walbec-DayDocuments-ModalBody', _walbecDayDocumentsModalBody(), 'DynamicDashboardLoader');
		tmpTemplateProvider.addTemplate('Walbec-DayDocuments-ModalRow', _walbecDayDocumentsModalRow(), 'DynamicDashboardLoader');
		tmpTemplateProvider.addTemplate('Walbec-DayDoc-ProjectName', _walbecDayDocProjectName(), 'DynamicDashboardLoader');
		this._documentLinkSupportInstalled = true;
		this.log.info(`DynamicDashboardLoader: installed Documents drill-down support (tabular Templated shim under ${tmpInstalledCount} template set(s) + modal templates).`);
	}

	/**
	 * Documents drill-down for a dashboard day-row. Opens a WIDE pict-section-modal listing the
	 * EXACT documents that rolled up into `pBucketDate` — pulled from the lake `Day` rows'
	 * `IDDocuments` list (already domain-scoped to this dashboard), not a DocumentDate-range
	 * fetch (which over-fetches every document type on that date). Each row shows the date
	 * (YYYY-MM-DD), the project name, the document title, and Open-in-tab + Download-PDF actions
	 * via HeadlightDocumentRender. Wired from the documentsLinkCell button:
	 *   onclick="_Pict.providers.DynamicDashboardLoader.openDayDocuments('2026-05-06T00:00:00.000Z', 'May 6')".
	 *
	 * @param {string} pBucketDate - The day-row's BucketDate (matches the lake rows' BucketDate).
	 * @param {string} [pLabel] - Human label for the modal title (the BucketLabel).
	 * @return {boolean} - False if the modal view is unavailable.
	 */
	openDayDocuments(pBucketDate, pLabel)
	{
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function')
		{
			this.log.warn('DynamicDashboardLoader: Pict-Section-Modal view is not registered; cannot open day documents.');
			return false;
		}
		// Opening another popup closes the first.
		if (typeof tmpModal.dismissModals === 'function')
		{
			tmpModal.dismissModals();
		}

		const tmpLabel = pLabel ? String(pLabel) : '';
		const tmpTitle = tmpLabel ? `Documents — ${tmpLabel}` : 'Documents';
		const tmpModalWidth = 'min(900px, 92vw)';
		const tmpBucketDate = String(pBucketDate || '').trim();
		const fShowMessage = (pMessage, pClass) =>
		{
			tmpModal.show({ title: tmpTitle, content: `<p class="${pClass || 'has-text-grey'}">${pMessage}</p>`, width: tmpModalWidth, buttons: [ { Hash: 'close', Label: 'Close' } ] });
		};
		if (!tmpBucketDate)
		{
			fShowMessage('No date on this row to look up documents.');
			return false;
		}

		// Collect the day's distinct document IDs from a set of lake rows (each lake row carries
		// a comma-separated IDDocuments list for its project/dimension bucket).
		const fCollectDocumentIDs = (pLakeRows) =>
		{
			const tmpSeen = {};
			const tmpIDs = [];
			for (const tmpRow of (Array.isArray(pLakeRows) ? pLakeRows : []))
			{
				// Aggregate tables carry IDDocuments (CSV); document-grain
				// tables (e.g. HmaRunAvg) carry IDDocument (singular).
				const tmpCSV = (tmpRow && tmpRow.IDDocuments != null) ? String(tmpRow.IDDocuments) :
					((tmpRow && tmpRow.IDDocument != null) ? String(tmpRow.IDDocument) : '');
				for (const tmpPart of tmpCSV.split(','))
				{
					const tmpID = parseInt(String(tmpPart).trim(), 10);
					if (Number.isInteger(tmpID) && tmpID > 0 && !tmpSeen[tmpID])
					{
						tmpSeen[tmpID] = true; tmpIDs.push(tmpID);
					}
				}
			}
			return tmpIDs;
		};

		// Fetch the exact Document records by ID (main api), then ASYNC-render the popup body so
		// the per-row `{~E:Project~}` name lookups + `{~DateFormat~}` resolve, then show it wide.
		const fShowDocuments = (pIDs) =>
		{
			if (!pIDs || pIDs.length === 0)
			{
				fShowMessage('No documents recorded for this day.');
				return;
			}
			this.pict.EntityProvider.getEntitySetPage(
				'Document',
				`FBV~Deleted~EQ~0~FBL~IDDocument~INN~${pIDs.join(',')}~FSF~DocumentDate~DESC~0`,
				0,
				pIDs.length,
				(pError, pRecords) =>
				{
					const tmpRecords = Array.isArray(pRecords) ? pRecords : [];
					if (pError)
					{
						this.log.warn(`DynamicDashboardLoader: failed to load documents for ${tmpBucketDate}: ${pError.message || pError}`);
						fShowMessage('Could not load documents for this day — see console.', 'has-text-danger');
						return;
					}
					if (tmpRecords.length === 0)
					{
						fShowMessage('No documents found for this day.');
						return;
					}
					this.pict.AppData.WalbecDayDocuments = { Label: tmpLabel, Count: tmpRecords.length, Records: tmpRecords };
					this.pict.parseTemplateByHash('Walbec-DayDocuments-ModalBody', this.pict.AppData.WalbecDayDocuments, (pRenderError, pContent) =>
					{
						const tmpContent = (!pRenderError && pContent) ? pContent : '<p class="has-text-danger">Could not render the documents list.</p>';
						tmpModal.show({ title: tmpTitle, content: tmpContent, width: tmpModalWidth, buttons: [ { Hash: 'close', Label: 'Close' } ] });
					});
				},
				'',
				'/1.0/',
			);
		};

		// Primary source: the post-filter lake rows the loader already stashed in memory.
		const tmpStashedRows = Array.isArray(this.pict.AppData.WalbecRecords) ? this.pict.AppData.WalbecRecords : [];
		const tmpDayRows = tmpStashedRows.filter((pRow) => pRow && String(pRow.BucketDate) === tmpBucketDate);
		if (tmpDayRows.length > 0)
		{
			fShowDocuments(fCollectDocumentIDs(tmpDayRows));
			return true;
		}

		// Fallback: the day isn't in the loaded page — fetch its lake rows directly from the
		// dashboard's lake recordset (URLPrefix + Entity resolved from the active route).
		const tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) || '';
		const tmpRecordSetMatch = tmpHash.match(/^#\/PSRS\/([^/]+)\/SpecificDashboard\//);
		const tmpRecordSet = tmpRecordSetMatch ? tmpRecordSetMatch[1] : null;
		const tmpConfig = (tmpRecordSet && this.pict.PictSectionRecordSet && this.pict.PictSectionRecordSet.recordSetProviderConfigurations) ?
			this.pict.PictSectionRecordSet.recordSetProviderConfigurations[tmpRecordSet] : null;
		const tmpLakeEntity = tmpConfig && tmpConfig.RecordSetMeadowEntity;
		const tmpLakeURLPrefix = (tmpConfig && tmpConfig.RecordSetURLPrefix) || '/1.0/';
		if (!tmpLakeEntity)
		{
			fShowDocuments([]);
			return true;
		}
		this.pict.EntityProvider.getEntitySetPage(
			tmpLakeEntity,
			`FBV~Deleted~EQ~0~FBV~BucketDate~EQ~${tmpBucketDate}`,
			0,
			1000,
			(pLakeError, pLakeRows) =>
			{
				if (pLakeError)
				{
					this.log.warn(`DynamicDashboardLoader: lake fallback fetch failed for ${tmpBucketDate}: ${pLakeError.message || pLakeError}`);
				}
				fShowDocuments(fCollectDocumentIDs(pLakeRows));
			},
			'',
			tmpLakeURLPrefix,
		);
		return true;
	}

	/**
	 * Fetches the AvailableDashboards AppData record for a given customer.
	 *
	 * @param {number} pCustomerID - The IDCustomer to look up.
	 * @return {Promise<{ Dashboards: Array<{ Hash: string, Title: string, RecordSet: string }> } | null>}
	 */
	async loadAvailableDashboards(pCustomerID)
	{
		if (!pCustomerID)
		{
			return null;
		}
		// First-class Dashboard registry, read DIRECTLY from the meadow
		// endpoint like any entity — row-level auth scopes the customer, FSF
		// sorts by Ordinal, and the API serves DataSource as a parsed object.
		const tmpRows = await new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON('Dashboards/FilteredTo/FSF~Ordinal~ASC~0/0/200', (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
		if (!Array.isArray(tmpRows) || tmpRows.length === 0)
		{
			return null;
		}
		// CustomerVisible=0 rows are internal-only (IDCustomer 1 sessions see
		// everything); the Dashboard entity uses Name (house convention) —
		// downstream consumers (chooser template, registerDashboard) read
		// Title.
		const tmpVisible = tmpRows
			.filter((pRow) => pRow.CustomerVisible == 1 || pCustomerID == 1)
			.map((pRow) => this._normalizeRegistryRow(pRow));
		if (tmpVisible.length === 0)
		{
			return null;
		}
		return { Dashboards: tmpVisible };
	}

	/**
	 * Shape a Dashboard registry row the way the wiring code expects: Name → Title, and List-mode config (Mode/Entity/
	 * ListColumns/SearchFields/ScopeFilter) hoisted from the DataSource bag back to the entry root.
	 *
	 * @param {Record<string, any>} pRow
	 * @return {Record<string, any>}
	 */
	_normalizeRegistryRow(pRow)
	{
		const tmpEntry = Object.assign({}, pRow, { Title: pRow.Name || pRow.Hash });
		if (typeof tmpEntry.DataSource === 'string')
		{
			try
			{
				tmpEntry.DataSource = JSON.parse(tmpEntry.DataSource);
			}
			catch (pError)
			{
				tmpEntry.DataSource = {};
			}
		}
		if (tmpEntry.DataSource && tmpEntry.DataSource.Mode === 'List')
		{
			Object.assign(tmpEntry, tmpEntry.DataSource);
			delete tmpEntry.DataSource;
		}
		else if (tmpEntry.DataSource && Object.keys(tmpEntry.DataSource).length === 0)
		{
			delete tmpEntry.DataSource;
		}
		return tmpEntry;
	}

	/**
	 * Put the dashboard's Description on its recordset config as `RecordSetHeaderDescription`, the line the title
	 * templates show under the title. Only when the host asks for it (`ShowDescription`), and never for a dashboard whose
	 * registry row sets `HideDescription` (at the root, or in its DataSource bag).
	 *
	 * @param {Record<string, any>} pRecordSetConfiguration
	 * @param {Record<string, any>} pEntry - A normalized registry row.
	 * @param {DashboardHostOptions} [pHost]
	 */
	_applyHeaderDescription(pRecordSetConfiguration, pEntry, pHost)
	{
		if (!pHost || (pHost.ShowDescription !== true) || !pEntry)
		{
			return;
		}
		const tmpDataSource = (pEntry.DataSource && typeof pEntry.DataSource === 'object') ? pEntry.DataSource : {};
		if ((pEntry.HideDescription === true) || (tmpDataSource.HideDescription === true))
		{
			return;
		}
		const tmpDescription = (typeof pEntry.Description === 'string') ? pEntry.Description.trim() : '';
		if (tmpDescription)
		{
			pRecordSetConfiguration.RecordSetHeaderDescription = tmpDescription;
		}
	}

	/**
	 * Install the dashboard-scoped styling once. Safe in any host page.
	 */
	installDashboardStyles()
	{
		if (this._dashboardStylesInstalled || !this.pict.CSSMap || typeof this.pict.CSSMap.addCSS !== 'function')
		{
			return;
		}
		this._dashboardStylesInstalled = true;
		this.pict.CSSMap.addCSS('DynamicDashboardLoader-Scoped-CSS', _SCOPED_DASHBOARD_CSS, 600);
		if (typeof this.pict.CSSMap.injectCSS === 'function')
		{
			this.pict.CSSMap.injectCSS();
		}
	}

	/**
	 * Register one dashboard on demand, for a host that embeds dashboards rather than wiring them all at login.
	 * Re-registering is how a host applies a different project or role filter: the recordset config is replaced.
	 *
	 * @param {string} pHash - The dashboard hash.
	 * @param {number} pCustomerID
	 * @param {DashboardHostOptions} [pHost]
	 * @return {Promise<Record<string, any>|null>} the registered entry, or null when it can't be found or registered
	 */
	async prepareDashboard(pHash, pCustomerID, pHost)
	{
		this.installDashboardStyles();
		this._installActionColumnSuppressor();
		const tmpRows = await new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(`Dashboards/FilteredTo/FBV~Hash~EQ~${encodeURIComponent(pHash)}/0/1`, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
		if (!Array.isArray(tmpRows) || tmpRows.length < 1)
		{
			this.log.warn(`DynamicDashboardLoader: no Dashboard registry row for ${pHash}.`);
			return null;
		}
		const tmpEntry = this._normalizeRegistryRow(tmpRows[0]);
		if (tmpEntry.Mode === 'List')
		{
			return this.registerListDashboard(tmpEntry, pCustomerID, pHost) ? tmpEntry : null;
		}
		const tmpManifest = await this.loadDashboardManifest(tmpEntry.Hash, pCustomerID);
		if (!tmpManifest)
		{
			return null;
		}
		return this.registerDashboard(tmpEntry, tmpManifest, pCustomerID, pHost) ? tmpEntry : null;
	}

	/**
	 * @param {string} pRecordSet
	 * @return {Promise<boolean>} resolves once the recordset's provider has initialized (true) or failed (false)
	 */
	whenProviderReady(pRecordSet)
	{
		return (this._providerReady && this._providerReady[pRecordSet]) || Promise.resolve(true);
	}

	/**
	 * Fetches a single DataDashboard ManifestSchema record by Hash + IDCustomer.
	 *
	 * @param {string} pHash - The dashboard hash.
	 * @param {number} pCustomerID - The IDCustomer to scope to.
	 * @return {Promise<Record<string, any> | null>} The parsed Manifest object, or null if not found.
	 */
	async loadDashboardManifest(pHash, pCustomerID)
	{
		if (!pHash || !pCustomerID)
		{
			return null;
		}
		const tmpURL = `ManifestSchemas/FilteredTo/FBV~Type~EQ~DataDashboard~FBV~Hash~EQ~${encodeURIComponent(pHash)}~FBV~IDCustomer~EQ~${pCustomerID}/0/1`;
		const tmpRecords = await new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(tmpURL, (pError, pResult) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pResult);
			});
		});
		if (!Array.isArray(tmpRecords) || tmpRecords.length === 0)
		{
			this.log.warn(`DynamicDashboardLoader: no ManifestSchema record found for Hash=${pHash} IDCustomer=${pCustomerID}.`);
			return null;
		}
		const tmpRecord = tmpRecords[0];
		let tmpManifest = tmpRecord.Manifest;
		if (typeof tmpManifest === 'string')
		{
			try
			{
				tmpManifest = JSON.parse(tmpManifest);
			}
			catch (pError)
			{
				this.log.error(`DynamicDashboardLoader: Manifest field of ManifestSchema ${tmpRecord.IDManifestSchema} could not be parsed as JSON.`, { error: pError.message });
				return null;
			}
		}
		return tmpManifest;
	}

	/**
	 * Registers a single dashboard's manifest + recordset config into the running
	 * pict-section-recordset instance. Idempotent — re-registering the same
	 * dashboard overwrites the prior entries.
	 *
	 * @param {{ Hash: string, Title: string, RecordSet: string }} pDashboardEntry
	 * @param {Record<string, any>} pManifest - The pict-section-recordset manifest object.
	 * @param {number} pCustomerID - The customer this registration is scoped to.
	 */
	registerDashboard(pDashboardEntry, pManifest, pCustomerID, pHost)
	{
		if (!pDashboardEntry || !pDashboardEntry.Hash || !pDashboardEntry.RecordSet)
		{
			this.log.warn(`DynamicDashboardLoader: registerDashboard called with invalid dashboard entry.`, pDashboardEntry);
			return false;
		}
		if (!pManifest || !pManifest.Scope)
		{
			this.log.warn(`DynamicDashboardLoader: registerDashboard called for ${pDashboardEntry.Hash} with invalid manifest (missing Scope).`);
			return false;
		}
		if (pManifest.Scope !== pDashboardEntry.Hash)
		{
			this.log.warn(`DynamicDashboardLoader: manifest Scope [${pManifest.Scope}] does not match dashboard Hash [${pDashboardEntry.Hash}]; using Hash as authoritative key.`);
			pManifest.Scope = pDashboardEntry.Hash;
		}
		// Install the Documents drill-down runtime support (tabular Templated metatemplate
		// shim + modal-content templates) once, before any dashboard form renders.
		this._installDocumentLinkSupport();

		// Keep the manifest by hash so the lake summary can run its declared GlobalSolvers
		// (config-driven aggregation — see _runAggregationSolvers).
		(this._dashboardManifests = this._dashboardManifests || {})[pManifest.Scope] = pManifest;

		const tmpRecordSet = this.pict.PictSectionRecordSet;
		if (!tmpRecordSet)
		{
			this.log.error(`DynamicDashboardLoader: PictSectionRecordSet service is not present on pict.`);
			return false;
		}

		// Register manifest into pict-section-recordset's manifest registry.
		// The MetaController's initialize() ran earlier and populated `manifests`
		// from settings.Manifests; we mirror that work for runtime entries.
		try
		{
			tmpRecordSet.generateManifestTableCells(pManifest);
		}
		catch (pError)
		{
			this.log.error(`DynamicDashboardLoader: generateManifestTableCells failed for ${pDashboardEntry.Hash}: ${pError.message || pError}`);
		}
		tmpRecordSet.manifestDefinitions[pManifest.Scope] = pManifest;
		tmpRecordSet.manifests[pManifest.Scope] = pManifest.Form ? pManifest : this.pict.newManyfest(pManifest);

		// Register the recordset config that the dashboard route will look up.
		// The pre-filter constrains every read to this dashboard's bucket records
		// for this customer; date-range filters compose on top.
		// Config-driven data source: a dashboard config may carry a DataSource block
		// (e.g. a private-data-lake table — Entity/URLPrefix/DefaultFilter/ScopeFilter).
		// Absent → the AppData AggregatedDashboardData behavior (unchanged).
		const tmpDataSource = pDashboardEntry.DataSource || null;
		const tmpDefaultFilter = (tmpDataSource && tmpDataSource.DefaultFilter) ||
			`FBV~Type~EQ~AggregatedDashboardData~FBV~AppHash~EQ~${pDashboardEntry.Hash}~FBV~IDCustomer~EQ~${pCustomerID}~FSF~CreateDate~ASC~0`;
		// When the user clicks Apply Filters, RecordSet-Filters.js's performSearch
		// rewrites the URL via RecordSetFilterURLTemplate-${pViewContext}. The
		// dashboard renders filters with ViewContext='Dashboard'. If we don't set
		// a template, the framework defaults to /PSRS/<rs>/Dashboard/FilteredTo/...
		// which (a) routes away from our SpecificDashboard form-mode render and
		// (b) drops the scope pre-filter. Pin the URL template back to
		// SpecificDashboard with the scope filter baked in — FilterExperience
		// (user-added clauses) gets appended automatically by performSearch.
		const tmpScopeFilter = (tmpDataSource && tmpDataSource.ScopeFilter != null) ?
			tmpDataSource.ScopeFilter :
			`FBV~Type~EQ~AggregatedDashboardData~FBV~AppHash~EQ~${pDashboardEntry.Hash}`;
		// PageSize here is an aggregation CAP, not a window: the dashboard rolls up the whole
		// filter-matched set (KPIs / charts / per-day table), so we pull all of it up to this
		// many rows. Config-driven via the DataSource (default 5000); real filter sets sit
		// under it → one page, pagination hidden, while a larger set paginates as a natural
		// "refine your filter" signal. Reused for filter-apply re-nav (performSearch), so the
		// cap survives filtering.
		const tmpDashboardPageSize = (tmpDataSource && tmpDataSource.PageSize) || 1000;
		const tmpDashboardURLTemplate = (pHost && pHost.URLTemplate) ||
			`/PSRS/${pDashboardEntry.RecordSet}/SpecificDashboard/${pDashboardEntry.Hash}/FilteredTo/${encodeURIComponent(tmpScopeFilter)}/0/${tmpDashboardPageSize}`;
		const tmpRecordSetConfiguration =
		{
			'RecordSet': pDashboardEntry.RecordSet,
			'Title': pDashboardEntry.Title || pDashboardEntry.RecordSet,
			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': (tmpDataSource && tmpDataSource.Entity) || 'AppData',
			'RecordSetDefaultFilter': tmpDefaultFilter,
			'RecordSetDashboardManifestOnly': true,
			'RecordSetDashboardDefaultManifest': pDashboardEntry.Hash,
			'RecordSetDashboardManifests': [ pDashboardEntry.Hash ],
			'RecordSetFilterURLTemplate-Dashboard': tmpDashboardURLTemplate,
			'RecordSetFilterURLTemplate-Default': tmpDashboardURLTemplate,
			'AvailableVerbs': [ 'Dashboard' ],
			'RecordSetURLPrefix': (tmpDataSource && tmpDataSource.URLPrefix) || '/1.0/',
		};
		// When the manifest opts into client-side filtering with HideSchemaFilters,
		// suppress every AppData column from auto-discovery so the picker only
		// shows our manifest-declared filters (Datum.* paths, etc). This is the
		// supported way — the provider reads RecordSetIgnoreFilterFields and
		// won't fold these into _FilterSchema during initializeFilterSchema.
		if (pManifest.ClientSide && pManifest.ClientSide.Enabled && pManifest.ClientSide.HideSchemaFilters)
		{
			tmpRecordSetConfiguration.RecordSetIgnoreFilterFields = [
				'IDAppData', 'GUIDAppData', 'CreateDate', 'CreatingIDUser',
				'UpdateDate', 'UpdatingIDUser', 'Deleted', 'DeleteDate', 'DeletingIDUser',
				'IDProject', 'Type', 'Title', 'Description', 'AppHash', 'IDCustomer', 'Datum',
			];
		}

		// Lake recordsets: the PK column (ID<PhysicalTable>) starts with 'ID', so the
		// recordset filter generator (getFieldFilterClauses) misreads it as a foreign
		// key and builds a self-join value-list whose Select2 fetch hits /1.0/<Table>s
		// — no lake URLPrefix → 404. Hide the key/audit/display columns so only the
		// business columns are offered as filters.
		if (tmpDataSource && /\/PrivateDataLake\//.test(tmpDataSource.URLPrefix || ''))
		{
			const tmpDomain = (String(tmpDataSource.URLPrefix).match(/PrivateDataLake\/([^/]+)\//) || [])[1] || '';
			const tmpPhysical = `C${pCustomerID}_${tmpDomain}_${tmpDataSource.Entity}`;
			tmpRecordSetConfiguration.RecordSetIgnoreFilterFields = [
				`ID${tmpPhysical}`, `GUID${tmpPhysical}`, 'BucketLabel', 'CreateDate', 'UpdateDate', 'Deleted',
			];
			// A lake table carries its source document id as DENORMALIZED DATA, not a
			// navigable reference. Without this the provider infers `IDDocument` is a
			// foreign key and bulk-reads the DECORATED /1.0/Documents endpoint with an
			// INN list of every distinct id on the page — a paged fan-out that fetches
			// records nothing renders and stalls the record bind. (pict-section-recordset
			// >= 1.25.10; supersedes the cacheConnectedEntityRecords monkey-patch in
			// _disableDeviceCaching for lake recordsets.)
			tmpRecordSetConfiguration.RecordSetIgnoreConnectedEntityFields = [ 'IDDocument' ];
		}

		// Apply the manifest's declarative `Filters` block to the recordset config
		// (SearchFields, QuickFilters, RecordSetIgnoreFilterFields) BEFORE the
		// provider initializes — those flow through as PSRS options.
		this._applyFilterConfigFromManifest(pManifest, tmpRecordSetConfiguration);
		// Register each Definition with FilterManager BEFORE the provider's
		// async schema init runs — PSRS folds them into the provider's
		// `_FilterSchema` during initializeFilterSchema (pict-section-recordset
		// ≥1.9.3 honours both CoreConnectionColumn-style and FilterByColumn-
		// style declarations, and the optional `RecordSet` field scopes a filter
		// to a single recordset). The host configures, PSRS renders the chrome.
		this._registerFilterDefinitions(pManifest, pDashboardEntry);
		this._applyHeaderDescription(tmpRecordSetConfiguration, pDashboardEntry, pHost);

		const tmpProvider = tmpRecordSet.loadRecordSetConfiguration(tmpRecordSetConfiguration);
		if (!tmpProvider)
		{
			this.log.error(`DynamicDashboardLoader: loadRecordSetConfiguration failed for ${pDashboardEntry.RecordSet}.`);
			return false;
		}
		// AppData.Datum is a String column holding JSON. Templates and chart
		// solvers reference Record.Data.Datum.<field> — which only works if
		// Datum is an object. Wrap getRecords to JSON.parse Datum on every
		// row before it reaches the renderer.
		this._installDatumParser(tmpProvider, pManifest.Scope);
		this._installHostFilter(tmpProvider, pHost && pHost.ExtraFilter);

		// Optional client-side filter mode: opt-in via manifest.ClientSide.Enabled.
		// Server returns scope-filtered records; user-added FilterClauses (from
		// the dashboard's Add Filters picker, including ones over Datum.* paths
		// the server can't reach via FBV) get applied locally against the parsed
		// records. Lets us expose Mix Design / Material / nested fields as
		// filterable without changing the data model.
		if (pManifest.ClientSide && pManifest.ClientSide.Enabled)
		{
			this._installClientSideFiltering(tmpProvider, pManifest, pDashboardEntry);
		}

		// Optional drill-down: register a sibling Document recordset and stamp
		// each bucket record with a DrillDownURL pointing into it. The Link
		// column in the detail table picks up that URL for its href.
		if (pManifest.DrillDown && pManifest.DrillDown.Enabled)
		{
			this._registerDrillDownRecordSet(pManifest, pDashboardEntry, pCustomerID);
			this._installDrillDownURLStamper(tmpProvider, pManifest, pDashboardEntry, pCustomerID);
		}
		// Initialize the freshly-registered provider so it can answer route requests. An embedding host renders straight
		// after registering, so it waits on whenProviderReady(); the data app wires at login and never has to.
		(this._providerReady = this._providerReady || {})[pDashboardEntry.RecordSet] = new Promise((fResolve) =>
		{
			try
			{
				if (typeof tmpProvider.initializeAsync !== 'function')
				{
					return fResolve(true);
				}
				tmpProvider.initializeAsync((pError) =>
				{
					if (pError)
					{
						this.log.error(`DynamicDashboardLoader: provider for ${pDashboardEntry.RecordSet} failed to initialize: ${pError.message || pError}`);
					}
					fResolve(!pError);
				});
			}
			catch (pError)
			{
				this.log.error(`DynamicDashboardLoader: initializeAsync threw for ${pDashboardEntry.RecordSet}: ${pError.message || pError}`);
				fResolve(false);
			}
		});
		return true;
	}

	/**
	 * AND a host's project and role filters onto every read a recordset provider makes — records, counts, inline reads,
	 * and the distinct-value reads behind filter pickers — so paging, searching and filtering can't drop them. The
	 * recordset's RecordSetDefaultFilter is only a fallback when a route names no filter, so it can't carry them.
	 * Re-registering a dashboard replaces the filter; an empty one turns the wrapper into a pass-through.
	 *
	 * @param {any} pProvider - The recordset provider.
	 * @param {string} [pExtraFilter] - Meadow filter clauses.
	 */
	_installHostFilter(pProvider, pExtraFilter)
	{
		if (!pProvider)
		{
			return;
		}
		const tmpNewHostFilter = pExtraFilter || '';
		if (pProvider._headlightHostFilterInstalled && pProvider._HeadlightHostFilter !== tmpNewHostFilter)
		{
			// Distinct values cached under the old host filter no longer apply.
			pProvider._scopeDistinctCache = {};
		}
		pProvider._HeadlightHostFilter = tmpNewHostFilter;
		if (pProvider._headlightHostFilterInstalled)
		{
			return;
		}
		pProvider._headlightHostFilterInstalled = true;
		const fWithHostFilter = (pFilterString) =>
		{
			const tmpHostFilter = pProvider._HeadlightHostFilter;
			if (!tmpHostFilter)
			{
				return pFilterString;
			}
			return (pFilterString && String(pFilterString).length > 0) ? `${tmpHostFilter}~${pFilterString}` : tmpHostFilter;
		};
		for (const tmpMethod of [ 'getRecords', 'getDecoratedRecords', 'getRecordSetCount' ])
		{
			if (typeof pProvider[tmpMethod] === 'function')
			{
				const fOriginal = pProvider[tmpMethod].bind(pProvider);
				pProvider[tmpMethod] = (pOptions, ...pRest) => fOriginal(Object.assign({}, pOptions, { FilterString: fWithHostFilter(pOptions && pOptions.FilterString) }), ...pRest);
			}
		}
		if (typeof pProvider.getRecordsInline === 'function')
		{
			const fOriginalInline = pProvider.getRecordsInline.bind(pProvider);
			pProvider.getRecordsInline = (pFilterString, ...pRest) => fOriginalInline(fWithHostFilter(pFilterString), ...pRest);
		}
		if (typeof pProvider.getRecordSetColumnDistinct === 'function')
		{
			const fOriginalDistinct = pProvider.getRecordSetColumnDistinct.bind(pProvider);
			pProvider.getRecordSetColumnDistinct = (pColumn, pOptions, fCallback, ...pRest) =>
			{
				let tmpOptions = pOptions;
				let tmpCallback = fCallback;
				if (typeof tmpOptions === 'function')
				{
					tmpCallback = tmpOptions;
					tmpOptions = {};
				}
				tmpOptions = tmpOptions || {};
				// The provider caches under `Column::<Filter>` using the filter it receives, which here
				// carries the host prefix. The recordset views peek at `_scopeDistinctCache` with the key
				// built from the filter THEY passed, and re-mount from the callback until that peek hits —
				// so mirror the result under the caller's key too, or they recurse forever.
				const tmpCallerKey = tmpOptions.Filter ? `${pColumn}::${tmpOptions.Filter}` : pColumn;
				return fOriginalDistinct(pColumn, Object.assign({}, tmpOptions, { Filter: fWithHostFilter(tmpOptions.Filter) }),
					(pError, pValues, ...pCallbackRest) =>
					{
						pProvider._scopeDistinctCache = pProvider._scopeDistinctCache || {};
						pProvider._scopeDistinctCache[tmpCallerKey] = Array.isArray(pValues) ? pValues : [];
						if (typeof tmpCallback === 'function')
						{
							return tmpCallback(pError, pValues, ...pCallbackRest);
						}
					}, ...pRest);
			};
		}
	}

	/**
	 * Wrap a recordset provider's `getRecords` so the Datum field on every
	 * returned record is a parsed object instead of a JSON string. Also stashes
	 * the parsed records and a per-dashboard summary aggregate at hyphen-free
	 * AppData addresses so form-mode KPI ValueSolvers / chart solvers can
	 * reference them as bare identifiers (expression parser cannot handle
	 * hyphenated property names like `PSRS-Dashboard-Data`).
	 *
	 * Idempotent — a second install on the same provider is a no-op.
	 *
	 * @param {any} pProvider - The recordset provider from loadRecordSetConfiguration.
	 * @param {string} pDashboardHash - The dashboard hash (manifest Scope) this provider serves.
	 */

	/**
	 * Apply the manifest's declarative `Filters` block to the recordset config
	 * object before `loadRecordSetConfiguration` runs, so SearchFields /
	 * QuickFilters / IgnoreFields are present when the provider initializes.
	 *
	 *   Filters: {
	 *     SearchFields: ['MixDesign', 'JMF'],
	 *     QuickFilters: ['BucketDate', 'IDProject', 'MixDesign'],
	 *     Definitions: { ... },          // ← handed off to _registerFilterDefinitions
	 *     IgnoreFields: ['BucketLabel', 'Result']
	 *   }
	 *
	 * Definitions themselves are NOT consumed here — they flow through
	 * `_registerFilterDefinitions` → FilterManager → PSRS's _FilterSchema fold-in.
	 * Safe no-op when `pManifest.Filters` is missing.
	 *
	 * @param {Record<string, any>} pManifest
	 * @param {Record<string, any>} pRecordSetConfiguration - mutated in place.
	 */
	_applyFilterConfigFromManifest(pManifest, pRecordSetConfiguration)
	{
		const tmpFilters = pManifest && pManifest.Filters;
		if (!tmpFilters || typeof tmpFilters !== 'object') return;
		if (Array.isArray(tmpFilters.SearchFields) && tmpFilters.SearchFields.length > 0)
		{
			pRecordSetConfiguration.SearchFields = tmpFilters.SearchFields.slice();
		}
		if (Array.isArray(tmpFilters.QuickFilters) && tmpFilters.QuickFilters.length > 0)
		{
			pRecordSetConfiguration.QuickFilters = tmpFilters.QuickFilters.slice();
		}
		if (Array.isArray(tmpFilters.IgnoreFields) && tmpFilters.IgnoreFields.length > 0)
		{
			const tmpExisting = Array.isArray(pRecordSetConfiguration.RecordSetIgnoreFilterFields) ?
				pRecordSetConfiguration.RecordSetIgnoreFilterFields :
				[];
			pRecordSetConfiguration.RecordSetIgnoreFilterFields = Array.from(new Set([ ...tmpExisting, ...tmpFilters.IgnoreFields ]));
		}
		// Project each Definition into RecordSetFieldFilterClauses[column].
		// PSRS reads this map in `getFieldFilterClauses` and uses the supplied
		// clause array INSTEAD OF auto-generating StringMatch / StringRange
		// clauses for that column — which is why our DateRange clause on
		// BucketDate would otherwise lose to the auto-emitted StringMatch
		// pair on a column the schema endpoint reports as `type: "string"`,
		// and why our InternalJoinSelectedValueList picker clauses on
		// IDProject / Product / MixDesign / JMF / Material would otherwise
		// sit next to an auto-emitted StringMatch entry that the popover
		// surfaces as a plain text input.
		const tmpDefs = tmpFilters.Definitions;
		if (tmpDefs && typeof tmpDefs === 'object')
		{
			const tmpFieldClauses = (pRecordSetConfiguration.RecordSetFieldFilterClauses &&
				typeof pRecordSetConfiguration.RecordSetFieldFilterClauses === 'object') ?
				pRecordSetConfiguration.RecordSetFieldFilterClauses :
				(pRecordSetConfiguration.RecordSetFieldFilterClauses = {});
			for (const tmpKey of Object.keys(tmpDefs))
			{
				const tmpDef = tmpDefs[tmpKey];
				if (!tmpDef || !tmpDef.Type || !tmpDef.FilterByColumn) continue;
				const tmpClause = Object.assign({}, tmpDef);
				if (!tmpClause.FilterKey) tmpClause.FilterKey = tmpDef.FilterByColumn;
				if (!tmpClause.ClauseKey)
				{
					tmpClause.ClauseKey = (tmpDef.Type === 'DateRange' || tmpDef.Type === 'NumericRange' || tmpDef.Type === 'StringRange') ?
						`${tmpDef.FilterByColumn}_Range` :
						`${tmpDef.FilterByColumn}_Match`;
				}
				if (!tmpClause.Label) tmpClause.Label = tmpClause.DisplayName || tmpKey;
				if (tmpClause.Ordinal == null) tmpClause.Ordinal = 1;
				tmpFieldClauses[tmpDef.FilterByColumn] = [ tmpClause ];
			}
		}
	}

	/**
	 * Register each `Filters.Definitions` entry with FilterManager. PSRS folds
	 * the resulting filters into the recordset's `_FilterSchema` during the
	 * provider's `initializeFilterSchema` pass (1.9.3+ recognises plain
	 * FilterByColumn-style filters and the optional per-recordset `RecordSet`
	 * scope).
	 *
	 *   Definitions: {
	 *     BucketDate: { Type: 'DateRange', FilterByColumn: 'BucketDate',
	 *                   DisplayName: 'Date', MinimumLabel: 'From', MaximumLabel: 'To' },
	 *     IDProject:  { Type: 'StringMatch', FilterByColumn: 'IDProject',
	 *                   DisplayName: 'Project',
	 *                   PictForm: { InputType: 'Picker', Entity: 'WalbecMoistureProjects',
	 *                               ValueField: 'IDProject', LabelField: 'ProjectName' } },
	 *     Product:    { ... }
	 *   }
	 *
	 * Each registered clause is stamped with `RecordSet` from the dashboard
	 * entry so PSRS only folds it into the matching provider — three lake
	 * dashboards can register a `Project` filter pointing at three different
	 * Picker entities without cross-pollination. Errors are logged but never
	 * thrown — a single bad filter shouldn't block dashboard registration.
	 *
	 * Legacy `FilterCriteria.Default` arrays are still consumed for back-compat
	 * with older manifests that pre-date the `Filters.Definitions` shape.
	 *
	 * @param {Record<string, any>} pManifest
	 * @param {{ Hash:string, RecordSet:string }} pDashboardEntry
	 */
	_registerFilterDefinitions(pManifest, pDashboardEntry)
	{
		try
		{
			const tmpFilterMgr = this.pict.providers.FilterManager;
			if (!tmpFilterMgr || typeof tmpFilterMgr.addFilter !== 'function') return;
			const tmpRecordSet = pDashboardEntry.RecordSet;
			const tmpDefinitions = (pManifest.Filters && pManifest.Filters.Definitions) || {};
			for (const tmpKey of Object.keys(tmpDefinitions))
			{
				const tmpDef = tmpDefinitions[tmpKey];
				if (!tmpDef || !tmpDef.Type) continue;
				const tmpClone = Object.assign({}, tmpDef);
				if (!tmpClone.FilterKey) tmpClone.FilterKey = tmpKey;
				if (!tmpClone.DisplayName) tmpClone.DisplayName = tmpClone.Label || tmpKey;
				// Per-dashboard scope: only fold into the matching recordset.
				if (!tmpClone.RecordSet) tmpClone.RecordSet = tmpRecordSet;
				// Namespace the FilterManager key by RecordSet so a second
				// dashboard registering the same Definition key doesn't clobber
				// the first (FilterManager.filters is global).
				const tmpRegistrationKey = `${tmpRecordSet}::${tmpKey}`;
				tmpFilterMgr.addFilter(tmpRegistrationKey, tmpClone);
			}
			// Legacy FilterCriteria.Default — back-compat for manifests pre-Filters.Definitions.
			const tmpLegacyDefs = (pManifest.FilterCriteria && pManifest.FilterCriteria.Default) || [];
			if (Array.isArray(tmpLegacyDefs))
			{
				for (let tmpIndex = 0; tmpIndex < tmpLegacyDefs.length; tmpIndex++)
				{
					const tmpDef = tmpLegacyDefs[tmpIndex];
					if (!tmpDef || !tmpDef.Type) continue;
					if (tmpDefinitions[tmpDef.FilterKey || tmpDef.FilterByColumn]) continue;
					const tmpClone = Object.assign({}, tmpDef);
					if (!tmpClone.DisplayName) tmpClone.DisplayName = tmpClone.Label || tmpClone.FilterKey || `legacy_${tmpIndex}`;
					if (!tmpClone.RecordSet) tmpClone.RecordSet = tmpRecordSet;
					const tmpKey = tmpClone.FilterKey || `${pManifest.Scope}_${tmpDef.Type}_${tmpDef.FilterByColumn || tmpIndex}`;
					tmpFilterMgr.addFilter(`${tmpRecordSet}::${tmpKey}`, tmpClone);
				}
			}
		}
		catch (pError)
		{
			this.log.warn(`DynamicDashboardLoader: failed to register filters for ${pDashboardEntry.Hash}: ${pError.message || pError}`);
		}
	}

	_installDatumParser(pProvider, pDashboardHash)
	{
		if (!pProvider || pProvider._datumParserInstalled)
		{
			return;
		}
		const tmpParseInPlace = (pRecords) =>
		{
			if (!Array.isArray(pRecords)) return;
			for (const tmpRecord of pRecords)
			{
				if (tmpRecord && typeof tmpRecord.Datum === 'string')
				{
					try
					{
						tmpRecord.Datum = JSON.parse(tmpRecord.Datum);
					}
					catch (pError)
					{
						// leave as-is; render renders empty rather than crashing
					}
				}
			}
		};

		const tmpStash = (pRecords) =>
		{
			if (!Array.isArray(pRecords)) return;
			// Document-form rendering elsewhere mutates DataBroker.marshalDestination
			// to a deep path (e.g. AppData.DocumentData.ReportData.FormData) and
			// never restores it. Chart / ValueSolver expressions then resolve bare
			// identifiers (WalbecRecords, WalbecSummary) against that stale scope
			// and find nothing. Reset here as well as in the chooser/route render —
			// covers direct-URL loads and back-button navigation.
			try
			{
				this.pict.providers.DataBroker.marshalDestination = 'AppData';
			}
			catch (pErr)
			{/* DataBroker may not be present */}
			// Charts and the detail table both want chronological order. The server
			// returns records sorted by ID/CreateDate which doesn't match the actual
			// bucket date (especially across day-rollover or republished buckets).
			// Sort in place by Datum.BucketDate ascending.
			pRecords.sort((pA, pB) =>
			{
				const tmpA = (pA && pA.Datum && pA.Datum.BucketDate) || '';
				const tmpB = (pB && pB.Datum && pB.Datum.BucketDate) || '';
				return tmpA < tmpB ? -1 : tmpA > tmpB ? 1 : 0;
			});
			// Hyphen-free aliases for expression-parser access. Only one dashboard
			// renders at a time, so a single shared slot is fine.
			this.pict.AppData.WalbecRecords = pRecords;
			this.pict.AppData.WalbecActiveDashboard = pDashboardHash;
			try
			{
				this.pict.AppData.WalbecSummary = this._computeDashboardSummary(pDashboardHash, pRecords);
			}
			catch (pError)
			{
				this.log.warn(`DynamicDashboardLoader: summary compute failed for ${pDashboardHash}: ${pError.message || pError}`);
				this.pict.AppData.WalbecSummary = {};
			}
			try
			{
				const tmpFirst = pRecords[0] && pRecords[0].Datum;
				const tmpDatumType = typeof tmpFirst;
				const tmpKeys = (tmpFirst && tmpDatumType === 'object') ? Object.keys(tmpFirst).slice(0, 8) : [];
				this.log.info(`DynamicDashboardLoader: stashed ${pRecords.length} record(s) for ${pDashboardHash}; Datum type=${tmpDatumType}, keys=${JSON.stringify(tmpKeys)}, summary=${JSON.stringify(this.pict.AppData.WalbecSummary)}`);
			}
			catch (pErr)
			{/* logging only */}
		};

		// Wrap getRecords (the primary fetch entry point on MeadowEndpoints
		// providers) to JSON-parse Datum before the result reaches callers.
		const tmpOriginalGetRecords = pProvider.getRecords.bind(pProvider);
		pProvider.getRecords = async (pOptions) =>
		{
			const tmpResult = await tmpOriginalGetRecords(pOptions);
			if (tmpResult)
			{
				tmpParseInPlace(tmpResult.Records);
				tmpStash(tmpResult.Records);
			}
			return tmpResult;
		};

		// getDecoratedRecords on the base class calls `this.getRecords`, which
		// resolves through the prototype chain to our wrapped version above —
		// but we override it explicitly here so dispatch is unambiguous.
		const tmpOriginalDecorated = pProvider.getDecoratedRecords ?
			pProvider.getDecoratedRecords.bind(pProvider) :
			null;
		pProvider.getDecoratedRecords = async (pOptions) =>
		{
			const tmpResult = tmpOriginalDecorated ?
				await tmpOriginalDecorated(pOptions) :
				await pProvider.getRecords(pOptions);
			if (tmpResult)
			{
				tmpParseInPlace(tmpResult.Records);
				tmpStash(tmpResult.Records);
			}
			return tmpResult;
		};

		const tmpOriginalInline = pProvider.getRecordsInline ?
			pProvider.getRecordsInline.bind(pProvider) :
			null;
		if (tmpOriginalInline)
		{
			pProvider.getRecordsInline = async (pFilterString, pOffset, pPageSize) =>
			{
				const tmpResult = await tmpOriginalInline(pFilterString, pOffset, pPageSize);
				if (tmpResult)
				{
					tmpParseInPlace(tmpResult.Records);
					tmpStash(tmpResult.Records);
				}
				return tmpResult;
			};
		}

		pProvider._datumParserInstalled = true;
	}

	/**
	 * Register a sibling pict-section-recordset configuration that targets
	 * the Document entity, scoped to the DocumentTypes a given dashboard
	 * aggregates. Dashboard rows then drill down to it via a per-row Link cell.
	 *
	 * Manifest opt-in shape:
	 *   ClientSide.Enabled       : not strictly required, but typical
	 *   DrillDown.Enabled        : must be truthy
	 *   DrillDown.RecordSet      : recordset key (defaults to `${dashboard}Docs`)
	 *   DrillDown.DocumentTypes  : Array<string> — DocumentTypes to include
	 *   DrillDown.Title          : label shown in URL / breadcrumb
	 *   DrillDown.ListColumns    : Array<{ Key, DisplayName }> for the Document list view
	 *
	 * @param {Record<string, any>} pManifest
	 * @param {{ Hash:string, RecordSet:string, Title:string }} pDashboardEntry
	 * @param {number} pCustomerID
	 */
	_registerDrillDownRecordSet(pManifest, pDashboardEntry, pCustomerID)
	{
		const tmpDD = pManifest.DrillDown || {};
		const tmpDocsRecordSet = tmpDD.RecordSet || `${pDashboardEntry.RecordSet}Docs`;
		const tmpDocumentTypes = Array.isArray(tmpDD.DocumentTypes) ? tmpDD.DocumentTypes : [];
		if (tmpDocumentTypes.length === 0)
		{
			this.log.warn(`DynamicDashboardLoader: drill-down for ${pDashboardEntry.Hash} declared without DocumentTypes; skipping registration.`);
			return;
		}

		// Filter clause: DocumentType IN (…) + IDCustomer = N. Meadow's URL filter
		// language uses FBV with EQ for single values and FBVOR/FBV chaining for
		// OR groups. For our short DocumentType lists we OR the EQ clauses and AND
		// in the customer scope.
		const tmpDocTypeOR = tmpDocumentTypes
			.map((pT, pIdx) => (pIdx === 0 ? `FBV~DocumentType~EQ~${pT}` : `FBVOR~DocumentType~EQ~${pT}`))
			.join('~');
		const tmpScopeFilter = `${tmpDocTypeOR}~FBV~IDCustomer~EQ~${pCustomerID}`;

		const tmpListColumns = Array.isArray(tmpDD.ListColumns) && tmpDD.ListColumns.length > 0 ?
			tmpDD.ListColumns :
			[
				{ Key: 'IDDocument', DisplayName: 'ID' },
				{ Key: 'DocumentName', DisplayName: 'Name' },
				{ Key: 'DocumentType', DisplayName: 'Type' },
				{ Key: 'DocumentDate', DisplayName: 'Date' },
			];

		const tmpRecordSetConfiguration =
		{
			'RecordSet': tmpDocsRecordSet,
			'Title': tmpDD.Title || `${pDashboardEntry.Title} — Source Documents`,
			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'Document',
			'RecordSetDefaultFilter': tmpScopeFilter,
			'RecordSetListColumns': tmpListColumns,
			'RecordSetIgnoreFilterFields': [ 'Deleted', 'DeletingIDUser', 'DeleteDate' ],
			'AvailableVerbs': [ 'List' ],
			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': tmpDD.SearchFields || [ 'DocumentName', 'DocumentType' ],
			// Replace the stock Actions column on document recordsets with two
			// buttons that route through HeadlightDocumentRender. The framework
			// installs `RecordSetListExtraColumnRowTemplateHash` per-recordset
			// from these templates, so customizing the Actions cell here is
			// scoped to this RecordSet only — no global template clobbering.
			'RecordSetListHasExtraColumns': true,
			'RecordSetListExtraColumnsHeaderTemplate': '<th class="has-text-centered">Document</th>',
			'RecordSetListExtraColumnRowTemplate': _walbecDocumentActionRowTemplate(),
		};

		try
		{
			this.pict.PictSectionRecordSet.loadRecordSetConfiguration(tmpRecordSetConfiguration);
			this.log.info(`DynamicDashboardLoader: registered drill-down recordset ${tmpDocsRecordSet} for ${pDashboardEntry.Hash} (DocumentTypes=${tmpDocumentTypes.join(', ')}).`);
		}
		catch (pError)
		{
			this.log.error(`DynamicDashboardLoader: failed to register drill-down recordset ${tmpDocsRecordSet}: ${pError.message || pError}`);
		}
	}

	/**
	 * Wrap the provider's stash so each parsed bucket record carries a
	 * `DrillDownURL` field pointing to the sibling Document recordset
	 * filtered to that bucket's BucketDate. The detail table's Link cell
	 * reads from this address.
	 *
	 * @param {any} pProvider
	 * @param {Record<string, any>} pManifest
	 * @param {{ Hash:string, RecordSet:string }} pDashboardEntry
	 * @param {number} pCustomerID
	 */
	_installDrillDownURLStamper(pProvider, pManifest, pDashboardEntry, pCustomerID)
	{
		const tmpDD = pManifest.DrillDown || {};
		const tmpDocsRecordSet = tmpDD.RecordSet || `${pDashboardEntry.RecordSet}Docs`;
		const tmpDocumentTypes = Array.isArray(tmpDD.DocumentTypes) ? tmpDD.DocumentTypes : [];
		if (tmpDocumentTypes.length === 0) return;

		const tmpDocTypeOR = tmpDocumentTypes
			.map((pT, pIdx) => (pIdx === 0 ? `FBV~DocumentType~EQ~${pT}` : `FBVOR~DocumentType~EQ~${pT}`))
			.join('~');

		const tmpStampOne = (pRecord) =>
		{
			if (!pRecord || !pRecord.Datum) return;
			const tmpBucketDate = pRecord.Datum.BucketDate;
			if (!tmpBucketDate) return;
			// DocumentDate values from meadow include time; LIKE-match against the
			// bucket-date prefix catches all timestamps in that calendar day. Build
			// the filter as a raw FBV string and URL-encode just once at the end.
			const tmpFilter = `${tmpDocTypeOR}~FBV~IDCustomer~EQ~${pCustomerID}~FBV~DocumentDate~LK~${tmpBucketDate}%`;
			pRecord.DrillDownURL = `#/PSRS/${tmpDocsRecordSet}/List/FilteredTo/${encodeURIComponent(tmpFilter)}`;
		};

		const tmpRewireFetchMethod = (pMethodName) =>
		{
			const tmpOriginal = pProvider[pMethodName] && pProvider[pMethodName].bind(pProvider);
			if (!tmpOriginal) return;
			pProvider[pMethodName] = async (...pArgs) =>
			{
				const tmpResult = await tmpOriginal(...pArgs);
				if (tmpResult && Array.isArray(tmpResult.Records))
				{
					for (const tmpRecord of tmpResult.Records) tmpStampOne(tmpRecord);
				}
				return tmpResult;
			};
		};
		tmpRewireFetchMethod('getRecords');
		tmpRewireFetchMethod('getDecoratedRecords');
		tmpRewireFetchMethod('getRecordsInline');
	}

	/**
	 * Mount the persistent bottom toolbar. The toolbar is a single fixed
	 * element appended to `document.body` once at boot — it never gets
	 * re-rendered by the recordset views, so we don't fight them, and we
	 * don't need a MutationObserver to keep it pinned.
	 *
	 * Updates flow through PictRouter's `after` hook: every successful route
	 * resolution triggers `_refreshBottomToolbar()`, which recomputes the
	 * left-side content (Back button + breadcrumb) and the visibility based
	 * on the current URL. Inline `onclick` attributes drive the buttons.
	 *
	 * Pagination "single-page hide" rides along on the same hook (after the
	 * recordset router finishes rendering, peek at the pagination DOM and
	 * hide the page-button UL when it has at most one entry).
	 */
	_mountBottomToolbar()
	{
		if (this._bottomToolbarMounted) return;
		this._bottomToolbarMounted = true;
		if (typeof document === 'undefined') return;

		// One-time DOM insert.
		let tmpToolbar = document.getElementById('walbec-bottom-toolbar');
		if (!tmpToolbar)
		{
			tmpToolbar = document.createElement('div');
			tmpToolbar.id = 'walbec-bottom-toolbar';
			tmpToolbar.className = 'walbec-bottom-toolbar';
			document.body.appendChild(tmpToolbar);
		}
		this._refreshBottomToolbar();

		// Trigger a refresh after every router resolution. Two channels:
		//
		//  (a) Wrap PictRouter's `navigate` so any in-app navigation fires our
		//      refresh after the framework finishes route dispatch. Function
		//      override, no event listeners.
		//
		//  (b) `window.onhashchange` / `window.onpopstate` property assignment
		//      (NOT addEventListener) catches back/forward/manual hash edits.
		//
		// Each refresh is debounced to one animation frame so a single
		// navigation doesn't run the same recompute twice if both channels fire.
		const tmpScheduleRefresh = (() =>
		{
			let tmpScheduled = false;
			return () =>
			{
				if (tmpScheduled) return;
				tmpScheduled = true;
				const tmpFrame = (typeof requestAnimationFrame === 'function') ? requestAnimationFrame : (pFn) => setTimeout(pFn, 16);
				tmpFrame(() =>
				{
					tmpScheduled = false;
					this._refreshBottomToolbar();
					this._hideSinglePagePagination();
				});
			};
		})();
		this._scheduleBottomToolbarRefresh = tmpScheduleRefresh;

		try
		{
			const tmpPictRouter = this.pict.providers.PictRouter && this.pict.providers.PictRouter.router;
			if (tmpPictRouter && typeof tmpPictRouter.navigate === 'function')
			{
				const tmpOriginalNavigate = tmpPictRouter.navigate.bind(tmpPictRouter);
				tmpPictRouter.navigate = function(...pArgs)
				{
					const tmpResult = tmpOriginalNavigate(...pArgs);
					tmpScheduleRefresh();
					return tmpResult;
				};
			}
			if (tmpPictRouter && typeof tmpPictRouter.resolve === 'function')
			{
				const tmpOriginalResolve = tmpPictRouter.resolve.bind(tmpPictRouter);
				tmpPictRouter.resolve = function(...pArgs)
				{
					const tmpResult = tmpOriginalResolve(...pArgs);
					tmpScheduleRefresh();
					return tmpResult;
				};
			}
		}
		catch (pError)
		{
			this.log.warn(`DynamicDashboardLoader: failed to wire router navigate hook for bottom toolbar: ${pError.message || pError}`);
		}

		if (typeof window !== 'undefined')
		{
			const tmpExistingHash = window.onhashchange;
			window.onhashchange = (pEvent) =>
			{
				if (typeof tmpExistingHash === 'function') tmpExistingHash(pEvent);
				tmpScheduleRefresh();
			};
			const tmpExistingPop = window.onpopstate;
			window.onpopstate = (pEvent) =>
			{
				if (typeof tmpExistingPop === 'function') tmpExistingPop(pEvent);
				tmpScheduleRefresh();
			};
		}
	}

	/**
	 * Refresh the bottom toolbar's inner HTML based on the current route.
	 * Hides the toolbar when on the chooser (no recordset list mounted).
	 */
	_refreshBottomToolbar()
	{
		const tmpToolbar = (typeof document !== 'undefined') && document.getElementById('walbec-bottom-toolbar');
		if (!tmpToolbar) return;
		const tmpHTML = this._buildBottomToolbarHTML();
		if (!tmpHTML)
		{
			tmpToolbar.style.display = 'none';
			tmpToolbar.innerHTML = '';
			return;
		}
		if (tmpToolbar.innerHTML !== tmpHTML) tmpToolbar.innerHTML = tmpHTML;
		tmpToolbar.style.display = '';
	}

	/**
	 * Single-page pagination is hidden purely via CSS (see DynamicDashboardLoader-CSS).
	 * Method retained as a no-op for any caller still wired through the route
	 * hook. Removing it entirely from the call sites is fine; this keeps the
	 * call site readable without re-routing.
	 */
	_hideSinglePagePagination()
	{
	}

	/**
	 * Compose the inner HTML of the bottom toolbar based on the current route.
	 * Returns an empty string if we shouldn't render the toolbar (e.g. on the
	 * chooser page).
	 *
	 * @return {string}
	 */
	_buildBottomToolbarHTML()
	{
		// Logout is shown on every route — it's the one control that must always
		// be reachable (chooser, custom landing, lists, dashboards).
		const tmpLogoutHTML = '' +
			'<button type="button" class="button is-small is-light" ' +
			'onclick="_Pict.providers.DynamicDashboardLoader.logout();" title="Sign out and return to the login screen">' +
			'<span class="icon"><i class="fa fa-arrow-right-from-bracket"></i></span><span>Logout</span></button>';

		const tmpContext = this._resolveDashboardRouteContext();
		if (!tmpContext)
		{
			// Chooser / custom landing / anything without dashboard context:
			// just the persistent Logout on the right.
			return `<div class="walbec-bottom-toolbar-left"></div><div class="walbec-bottom-toolbar-right">${tmpLogoutHTML}</div>`;
		}

		// Left side — back button + (optional) breadcrumb.
		let tmpLeftHTML = '';
		if (tmpContext.kind === 'dashboard')
		{
			tmpLeftHTML =
				'<a class="button is-small is-light" onclick="_Pict.views.DashboardChooser.render(); window.location.hash=\'\';" title="Back to dashboard list">' +
					'<span class="icon"><i class="fa fa-chevron-left"></i></span>' +
					'<span>Dashboards</span>' +
				'</a>';
		}
		else if (tmpContext.kind === 'documents')
		{
			const tmpBack = tmpContext.SourceURL ?
				`onclick="window.location.hash='${tmpContext.SourceURL.replace(/^#/, '')}';"` :
				'onclick="_Pict.views.DashboardChooser.render(); window.location.hash=\'\';"';
			const tmpBackLabel = tmpContext.SourceTitle || 'Dashboards';
			tmpLeftHTML =
				`<a class="button is-small is-light" ${tmpBack} title="Back to ${tmpBackLabel}">` +
					'<span class="icon"><i class="fa fa-chevron-left"></i></span>' +
					`<span>${this._escapeHTML(tmpBackLabel)}</span>` +
				'</a>';
			if (tmpContext.BreadcrumbBucket || tmpContext.SourceTitle)
			{
				tmpLeftHTML += '<nav class="breadcrumb is-small walbec-breadcrumb" aria-label="breadcrumbs"><ul>';
				tmpLeftHTML += `<li><a onclick="_Pict.views.DashboardChooser.render(); window.location.hash='';">Dashboards</a></li>`;
				if (tmpContext.SourceTitle && tmpContext.SourceURL)
				{
					tmpLeftHTML += `<li><a href="${tmpContext.SourceURL}">${this._escapeHTML(tmpContext.SourceTitle)}</a></li>`;
				}
				if (tmpContext.BreadcrumbBucket)
				{
					tmpLeftHTML += `<li class="is-active"><a aria-current="page">${this._escapeHTML(tmpContext.BreadcrumbBucket)}</a></li>`;
				}
				tmpLeftHTML += '</ul></nav>';
			}
		}

		// Right side — exports + logout. Excel = a real .xlsx, one worksheet per
		// on-screen table plus a Report Info sheet, with measurements as NUMBERS
		// rounded to each column's declared precision. CSV = the on-screen per-day
		// roll-up as text (the lossless escape hatch -- it keeps the stored string
		// untouched rather than rounding). Raw CSV = the unaggregated lake rows.
		// Print is first: on a dashboard this is the submittal action, and the CSV
		// exports are the data-wrangling ones. Only offered on an actual dashboard
		// -- there is nothing submittal-shaped about the document drill-down list.
		const tmpPrintHTML = (tmpContext.kind === 'dashboard') ? '' +
			'<button type="button" class="button is-small is-light" ' +
			'onclick="_Pict.providers.DynamicDashboardLoader.printDashboard()" title="Print this dashboard as a submittal (choose Save as PDF in the print dialog)">' +
			'<span class="icon"><i class="fa fa-print"></i></span><span>Print</span></button>' +
			'<span class="walbec-toolbar-divider"></span>' : '';

		const tmpRightHTML = '' +
			tmpPrintHTML +
			'<button type="button" class="button is-link is-small is-light" ' +
			'onclick="_Pict.providers.DashboardExporter.exportExcel()" title="Download this dashboard as an Excel workbook, one sheet per table">' +
			'<span class="icon"><i class="fa fa-file-excel"></i></span><span>Excel</span></button>' +
			'<button type="button" class="button is-small is-light" ' +
			'onclick="_Pict.providers.DashboardExporter.exportCSV()" title="Download the visible dashboard table as CSV">' +
			'<span class="icon"><i class="fa fa-file-csv"></i></span><span>CSV</span></button>' +
			'<button type="button" class="button is-small is-light" ' +
			'onclick="_Pict.providers.DashboardExporter.exportRawCSV()" title="Download the raw, unaggregated backing rows as CSV">' +
			'<span class="icon"><i class="fa fa-table"></i></span><span>Raw CSV</span></button>' +
			'<span class="walbec-toolbar-divider"></span>' +
			tmpLogoutHTML;

		return `<div class="walbec-bottom-toolbar-left">${tmpLeftHTML}</div><div class="walbec-bottom-toolbar-right">${tmpRightHTML}</div>`;
	}

	/**
	 * A readable summary of the filters currently applied, for the submittal header.
	 *
	 * This is the single most important line on a printed submittal: a page of
	 * control charts means nothing to WisDOT/MDOT unless it states WHICH mix design,
	 * WHICH site and WHICH dates it covers. Read from the live clause state rather
	 * than the URL so it reflects exactly what produced the numbers on the page.
	 *
	 * @param {string} pRecordSet - the record set the dashboard is bound to.
	 * @return {string} e.g. 'Mix Design: 506723  |  Date Sampled: 2025-10-06 to 2026-10-06'
	 */
	_resolvePrintFilterSummary(pRecordSet)
	{
		const tmpProvider = this.pict.providers['RSP-Provider-' + pRecordSet];
		if (!tmpProvider || (typeof tmpProvider.getFilterClauses !== 'function')) { return ''; }

		const tmpParts = [];
		for (const tmpClause of (tmpProvider.getFilterClauses() || []))
		{
			const tmpField = tmpClause.FilterByColumn || String(tmpClause.QuickFilterKey || '').replace(/^Quick-/, '');
			// The clause carries the label the user actually sees, which is the
			// customer's own wording ("Mix Design", not the column name MixDesign) --
			// and on a submittal that distinction is the difference between a
			// readable cover line and a dump of internal field names. Do NOT reach
			// for getQuickFilterDefinitions() here: it is populated by the filter
			// view's render pass and is empty at print time.
			const tmpLabel = tmpClause.Label || tmpClause.DisplayName || tmpField;
			const tmpValues = tmpClause.Values;
			if (Array.isArray(tmpValues))
			{
				if (tmpValues.length > 0) { tmpParts.push(`${tmpLabel}: ${tmpValues.join(', ')}`); }
			}
			else if (tmpValues && (typeof tmpValues === 'object'))
			{
				// A date range prints as its bounds, with an open end said out loud
				// rather than left blank -- "from X" and "X to Y" cover different data.
				const tmpStart = tmpValues.Start || '';
				const tmpEnd = tmpValues.End || '';
				if (tmpStart && tmpEnd) { tmpParts.push(`${tmpLabel}: ${tmpStart} to ${tmpEnd}`); }
				else if (tmpStart) { tmpParts.push(`${tmpLabel}: from ${tmpStart}`); }
				else if (tmpEnd) { tmpParts.push(`${tmpLabel}: through ${tmpEnd}`); }
			}
			else if (tmpValues) { tmpParts.push(`${tmpLabel}: ${tmpValues}`); }
		}
		return tmpParts.join('   |   ');
	}

	/**
	 * Print the current dashboard as a submittal.
	 *
	 * Built fresh at print time and torn down afterwards, deliberately: the record
	 * set re-renders its container on every search, so a header injected once would
	 * be wiped by the next filter change and silently print stale filter text on a
	 * document that goes to a DOT.
	 */
	printDashboard()
	{
		if (typeof document === 'undefined') { return; }
		const tmpContext = this._resolveDashboardRouteContext();
		const tmpContainer = document.getElementById('PRSP_Container');
		if (!tmpContainer) { return; }

		const tmpTitleElement = document.getElementById('PRSP_Title');
		const tmpTitle = (tmpTitleElement && tmpTitleElement.textContent.trim()) || 'Dashboard';
		const tmpSummary = tmpContext ? this._resolvePrintFilterSummary(tmpContext.RecordSet) : '';
		// The on-screen title row is hidden in print (its title would duplicate the
		// one above), so the record count it carries is lifted into the header --
		// a submittal has to say how many samples it covers.
		let tmpScope = '';
		const tmpTitleContainer = document.getElementById('PRSP_Title_Container');
		if (tmpTitleContainer)
		{
			const tmpMatch = tmpTitleContainer.textContent.match(/Showing[^.]*\./);
			if (tmpMatch) { tmpScope = tmpMatch[0].trim(); }
		}
		const tmpGenerated = new Date();
		const tmpStamp = `${tmpGenerated.getFullYear()}-${String(tmpGenerated.getMonth() + 1).padStart(2, '0')}-${String(tmpGenerated.getDate()).padStart(2, '0')}`;

		// Remove any leftover from an earlier print that never cleaned up.
		const tmpExisting = document.getElementById('WalbecPrintHead');
		if (tmpExisting && tmpExisting.parentElement) { tmpExisting.parentElement.removeChild(tmpExisting); }

		const tmpHead = document.createElement('div');
		tmpHead.id = 'WalbecPrintHead';
		tmpHead.className = 'walbec-printhead';
		tmpHead.innerHTML =
			`<div class="walbec-printhead-title">${this._escapeHTML(tmpTitle)}</div>` +
			(tmpSummary ? `<div class="walbec-printhead-filters">${this._escapeHTML(tmpSummary)}</div>`
				: '<div class="walbec-printhead-filters walbec-printhead-unfiltered">No filters applied &mdash; this report covers ALL records.</div>') +
			`<div class="walbec-printhead-stamp">${tmpScope ? this._escapeHTML(tmpScope) + '  &middot;  ' : ''}Generated ${tmpStamp}</div>`;
		tmpContainer.insertBefore(tmpHead, tmpContainer.firstChild);

		// The browser uses document.title for the PDF filename and the page header,
		// so a submittal saves as something meaningful instead of "HeadLight Data App".
		const tmpPreviousTitle = document.title;
		const tmpFilenameBits = [ tmpTitle.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, ''), tmpStamp ];
		document.title = tmpFilenameBits.join('_');

		const fCleanup = () =>
		{
			document.title = tmpPreviousTitle;
			const tmpPlanted = document.getElementById('WalbecPrintHead');
			if (tmpPlanted && tmpPlanted.parentElement) { tmpPlanted.parentElement.removeChild(tmpPlanted); }
			window.removeEventListener('afterprint', fCleanup);
		};
		window.addEventListener('afterprint', fCleanup);
		// afterprint does not fire on every cancel path in every browser, so the
		// page must not be left with a print header stuck to it.
		setTimeout(fCleanup, 60000);

		window.print();
	}

	/**
	 * Sign the current user out by calling `/1.0/Deauthenticate` on the data
	 * API, then reload the page so the login splash takes over. The Deauth
	 * endpoint accepts both GET and POST in our environments — use GET via
	 * the existing rest client so any per-host headers (X-Upstream, etc.)
	 * still apply. Falls back to a credentialed `fetch` if the rest client
	 * isn't available.
	 *
	 * Provided here on the loader because it's the natural home for the
	 * bottom-toolbar wiring; views call it via `_Pict.providers.DynamicDashboardLoader.logout()`.
	 */
	logout()
	{
		const tmpFinish = () =>
		{
			try
			{
				if (typeof window !== 'undefined' && window.location)
				{
					window.location.hash = '';
					window.location.reload();
				}
			}
			catch (pError)
			{/* navigation race — login splash will take over */}
		};

		try
		{
			const tmpClient = this.pict && this.pict.HeadlightRestClient;
			if (tmpClient && typeof tmpClient.getJSON === 'function')
			{
				tmpClient.getJSON('Deauthenticate', () => tmpFinish());
				return;
			}
		}
		catch (pError)
		{/* fall through to fetch */}

		if (typeof fetch === 'function')
		{
			fetch('/1.0/Deauthenticate', { credentials: 'include' })
				.catch(() =>
				{/* still log out locally */})
				.finally(() => tmpFinish());
			return;
		}
		tmpFinish();
	}

	/**
	 * Inspect the current URL hash and return a structured description of what
	 * the user is looking at. Used by the bottom toolbar to choose between
	 * "Back to Dashboards" and "Back to {Source Dashboard}" + breadcrumb.
	 *
	 * @return {{ kind:'dashboard'|'documents', RecordSet:string, SourceTitle?:string, SourceURL?:string, BreadcrumbBucket?:string } | null}
	 */
	_resolveDashboardRouteContext()
	{
		if (typeof window === 'undefined' || !window.location) return null;
		const tmpHash = window.location.hash || '';
		const tmpDashboardMatch = tmpHash.match(/^#\/PSRS\/([^\/]+)\/SpecificDashboard\/([^\/]+)/);
		if (tmpDashboardMatch)
		{
			return { kind: 'dashboard', RecordSet: tmpDashboardMatch[1], DashboardHash: tmpDashboardMatch[2] };
		}
		const tmpListMatch = tmpHash.match(/^#\/PSRS\/([^\/]+)\/List/);
		if (tmpListMatch)
		{
			const tmpRecordSet = tmpListMatch[1];
			// Try to map this Document recordset back to its source dashboard
			// so the user sees a meaningful "back" target. The drill-down sibling
			// is typically named `${SourceRecordSet}Docs`.
			const tmpResolved = { kind: 'documents', RecordSet: tmpRecordSet };
			const tmpSourceDashboard = this._findSourceDashboardForDocumentRecordSet(tmpRecordSet);
			if (tmpSourceDashboard)
			{
				tmpResolved.SourceTitle = tmpSourceDashboard.Title;
				const tmpScope = `FBV~Type~EQ~AggregatedDashboardData~FBV~AppHash~EQ~${tmpSourceDashboard.Hash}`;
				tmpResolved.SourceURL = `#/PSRS/${tmpSourceDashboard.RecordSet}/SpecificDashboard/${tmpSourceDashboard.Hash}/FilteredTo/${encodeURIComponent(tmpScope)}`;
			}
			// Sniff out the bucket date from the URL filter — drill-down stamps
			// `DocumentDate~LK~YYYY-MM-DD%` and we mirror that text into the
			// breadcrumb so the user knows which bucket spawned the list.
			const tmpDateMatch = tmpHash.match(/DocumentDate~LK~(\d{4}-\d{2}-\d{2})/);
			if (tmpDateMatch)
			{
				tmpResolved.BucketDate = tmpDateMatch[1];
				tmpResolved.BreadcrumbBucket = `Documents for ${tmpDateMatch[1]}`;
			}
			return tmpResolved;
		}
		return null;
	}

	/**
	 * Map a Document-list recordset back to the dashboard it drilled in from.
	 * Looks at every registered AvailableDashboards entry and matches by the
	 * `DrillDown.RecordSet` field on its loaded ManifestSchema.
	 *
	 * @param {string} pRecordSet
	 * @return {{ Hash:string, Title:string, RecordSet:string } | null}
	 */
	_findSourceDashboardForDocumentRecordSet(pRecordSet)
	{
		const tmpAvail = this.pict.AppData && this.pict.AppData.AvailableDashboards;
		if (!tmpAvail || !Array.isArray(tmpAvail.Dashboards)) return null;
		for (const tmpEntry of tmpAvail.Dashboards)
		{
			if (tmpEntry.Mode === 'List') continue;
			const tmpManifest = this.pict.PictSectionRecordSet &&
				this.pict.PictSectionRecordSet.manifestDefinitions &&
				this.pict.PictSectionRecordSet.manifestDefinitions[tmpEntry.Hash];
			if (!tmpManifest || !tmpManifest.DrillDown) continue;
			const tmpDocsRS = tmpManifest.DrillDown.RecordSet || `${tmpEntry.RecordSet}Docs`;
			if (tmpDocsRS === pRecordSet)
			{
				return { Hash: tmpEntry.Hash, Title: tmpEntry.Title, RecordSet: tmpEntry.RecordSet };
			}
		}
		return null;
	}

	/**
	 * Tiny escape — used only in template strings the toolbar pastes into
	 * innerHTML, so we cover the structurally dangerous chars.
	 *
	 * @param {string} pStr
	 */
	_escapeHTML(pStr)
	{
		return String(pStr == null ? '' : pStr)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	/**
	 * Register a List-mode "dashboard" — really a server-side recordset list
	 * (Documents). No ManifestSchema fetch, no client-side fu, just a clean
	 * RecordSetListColumns config so users can browse and filter the data via
	 * the standard pict-section-recordset list UI.
	 *
	 * `DashboardManifests` and `RecordDecoration` are what a recordset needs to be more than
	 * a flat browse: the manifests its Dashboard verb can render, and the related-entity
	 * pulls that decorate each row. They used to be reachable only from the app's hard-coded
	 * `DefaultRecordSetConfigurations`, which is why a per-customer landing recordset had to
	 * be compiled into the app. Both are optional and default to today's behaviour.
	 *
	 * There is deliberately no `Verbs` key. `AvailableVerbs` is not read by anything in
	 * pict-section-recordset -- `RecordSet-Router` registers List/Read/Create/Dashboard/
	 * Associate unconditionally for every recordset -- so accepting one here would be a
	 * config key that silently does nothing. Which verb a dashboard opens is decided by the
	 * route the chooser navigates to, not by this configuration.
	 *
	 * @param {{ Hash:string, Title:string, RecordSet:string, Entity?:string, ScopeFilter?:string, ListColumns?:Array, SearchFields?:Array, Filters?:Record<string, any>, DashboardManifests?:Array<string>, RecordDecoration?:Array<Object>, HideRowActions?:boolean }} pEntry
	 * @param {number} pCustomerID
	 */
	registerListDashboard(pEntry, pCustomerID, pHost)
	{
		if (!pEntry || !pEntry.RecordSet || !pEntry.Entity)
		{
			this.log.warn(`DynamicDashboardLoader: registerListDashboard called with invalid entry — Entity required.`);
			return false;
		}
		const tmpRecordSet = this.pict.PictSectionRecordSet;
		if (!tmpRecordSet)
		{
			this.log.error(`DynamicDashboardLoader: PictSectionRecordSet service is not present on pict.`);
			return false;
		}
		// PrivateDataLake tables sit behind the lake proxy and have NO IDCustomer
		// column (the C<n>_ table-name prefix IS the customer silo); customer
		// scope comes from the registration row's row-auth. Platform entities
		// (Document, …) at /1.0/ keep the IDCustomer scope clause. List mode is
		// the only server-paginated browse path, so lake tables too big to pull
		// client-side (the manifest/aggregate path) rely on it.
		const tmpURLPrefix = pEntry.URLPrefix || '/1.0/';
		const tmpIsLake = /\/PrivateDataLake\//.test(tmpURLPrefix);
		const tmpScopeBase = tmpIsLake ?
			(pEntry.ScopeFilter || '') :
			(pEntry.ScopeFilter && pEntry.ScopeFilter.length > 0 ?
				`${pEntry.ScopeFilter}~FBV~IDCustomer~EQ~${pCustomerID}` :
				`FBV~IDCustomer~EQ~${pCustomerID}`);
		const tmpRecordSetConfiguration =
		{
			'RecordSet': pEntry.RecordSet,
			'Title': pEntry.Title || pEntry.RecordSet,
			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': pEntry.Entity,
			'RecordSetDefaultFilter': tmpScopeBase,
			'RecordSetListColumns': pEntry.ListColumns || [],
			'RecordSetIgnoreFilterFields': [ 'Deleted', 'DeletingIDUser', 'DeleteDate' ],
			'AvailableVerbs': [ 'List' ],
			'RecordSetURLPrefix': tmpURLPrefix,
			'SearchFields': pEntry.SearchFields || [ 'Name' ],
		};
		// Set only when supplied. An empty array is not the same as absent to
		// pict-section-recordset, so assigning unconditionally would hand every list-mode
		// dashboard an empty manifest list and an empty decoration list.
		if (Array.isArray(pEntry.DashboardManifests) && pEntry.DashboardManifests.length > 0)
		{
			tmpRecordSetConfiguration.RecordSetDashboardManifests = pEntry.DashboardManifests;
		}
		if (Array.isArray(pEntry.RecordDecoration) && pEntry.RecordDecoration.length > 0)
		{
			tmpRecordSetConfiguration.RecordDecorationConfiguration = pEntry.RecordDecoration;
		}
		if (pEntry.HideRowActions === true)
		{
			tmpRecordSetConfiguration.RecordSetListHideRowActions = true;
		}
		// A `Filters` block works as it does on a manifest dashboard (SearchFields, QuickFilters, IgnoreFields, Definitions).
		if (pEntry.Filters && typeof pEntry.Filters === 'object')
		{
			this._applyFilterConfigFromManifest({ Filters: pEntry.Filters }, tmpRecordSetConfiguration);
			this._registerFilterDefinitions({ Filters: pEntry.Filters }, pEntry);
		}
		// Lake list: the PK column (ID<PhysicalTable>) starts with 'ID', so the
		// recordset filter generator misreads it as a foreign key and fires a
		// Select2 value-list fetch at /1.0/<Table>s (no lake prefix → 404). Hide
		// the key/audit columns from filter discovery — mirrors the chart path.
		if (tmpIsLake)
		{
			const tmpDomain = (tmpURLPrefix.match(/PrivateDataLake\/([^/]+)\//) || [])[1] || '';
			const tmpPhysical = `C${pCustomerID}_${tmpDomain}_${pEntry.Entity}`;
			tmpRecordSetConfiguration.RecordSetIgnoreFilterFields = [ `ID${tmpPhysical}`, `GUID${tmpPhysical}` ];
		}
		// Document recordsets pick up the same Open / PDF action column as
		// the drill-down lists do — keeps the Actions UI consistent across
		// every Document-backed recordset on the dashboard side of the app.
		if (pEntry.Entity === 'Document')
		{
			tmpRecordSetConfiguration.RecordSetListHasExtraColumns = true;
			tmpRecordSetConfiguration.RecordSetListExtraColumnsHeaderTemplate = '<th class="has-text-centered">Document</th>';
			tmpRecordSetConfiguration.RecordSetListExtraColumnRowTemplate = _walbecDocumentActionRowTemplate();
		}
		this._applyHeaderDescription(tmpRecordSetConfiguration, pEntry, pHost);
		try
		{
			const tmpProvider = tmpRecordSet.loadRecordSetConfiguration(tmpRecordSetConfiguration);
			this._installHostFilter(tmpProvider || this.pict.providers[`RSP-Provider-${pEntry.RecordSet}`], pHost && pHost.ExtraFilter);
			this.log.info(`DynamicDashboardLoader: registered List-mode dashboard ${pEntry.Hash} (${pEntry.RecordSet} → ${pEntry.Entity}).`);
			return true;
		}
		catch (pError)
		{
			this.log.error(`DynamicDashboardLoader: failed to register List dashboard ${pEntry.Hash}: ${pError.message || pError}`);
			return false;
		}
	}

	/**
	 * Hook the recordset list view's onBeforeRender so we can re-stamp the
	 * framework's stock Actions cell + header templates to empty whenever the
	 * route lands on one of our Document recordsets. Mirrors the approach in
	 * `ImprovedTabularRecordSetProvider.prepareTable` — no MutationObserver,
	 * just a function override on a single Pict view.
	 *
	 * Must run AFTER ImprovedTabularRecordSetProvider's hook (which is what
	 * stamps the default View template); our chain wraps theirs so we get the
	 * last word per render.
	 */
	_installActionColumnSuppressor()
	{
		if (this._actionColumnSuppressorInstalled) return;
		this._actionColumnSuppressorInstalled = true;
		const tmpView = this.pict.views && this.pict.views['PRSP-List-RecordList'];
		if (!tmpView || typeof tmpView.onBeforeRender !== 'function') return;

		const tmpOriginal = tmpView.onBeforeRender.bind(tmpView);
		tmpView.onBeforeRender = (pCallback, pRenderable) =>
		{
			tmpOriginal(pCallback, pRenderable);
			this._maybeSuppressDefaultActionColumn();
		};

		// The dashboard list view has its own stock Actions column. Same trick.
		const tmpDashboardView = this.pict.views && this.pict.views['PRSP-Dashboard-RecordList'];
		if (tmpDashboardView && typeof tmpDashboardView.onBeforeRender === 'function')
		{
			const tmpOriginalDash = tmpDashboardView.onBeforeRender.bind(tmpDashboardView);
			tmpDashboardView.onBeforeRender = (pCallback, pRenderable) =>
			{
				tmpOriginalDash(pCallback, pRenderable);
				this._maybeSuppressDefaultActionColumn();
			};
		}

		// A dashboard host renders lists in place, off the #/PSRS/ route, so remember which record set each list
		// view is about to paint; onBeforeRenderList runs just before the child views above render.
		const tmpChildViews = (this.pict.PictSectionRecordSet && this.pict.PictSectionRecordSet.childViews) || {};
		for (const tmpParentView of [ tmpChildViews.list, tmpChildViews.dashboard ])
		{
			if (!tmpParentView || typeof tmpParentView.onBeforeRenderList !== 'function')
			{
				continue;
			}
			const tmpOriginalBeforeList = tmpParentView.onBeforeRenderList.bind(tmpParentView);
			tmpParentView.onBeforeRenderList = (pRecordListData) =>
			{
				this._recordSetBeingPainted = (pRecordListData && pRecordListData.RecordSet) || null;
				return tmpOriginalBeforeList(pRecordListData);
			};
		}
	}

	/**
	 * Overwrite the framework's stock Actions cell + header templates with
	 * empty strings when the list being painted is one of our Document
	 * recordsets (drill-down `*Docs` or the All Documents list), a lake
	 * recordset, or a List-mode dashboard whose record sets `HideRowActions`.
	 * Otherwise leaves them alone, so other recordsets (PhysicalAsset etc.)
	 * keep their default View link.
	 */
	_maybeSuppressDefaultActionColumn()
	{
		try
		{
			const tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) || '';
			// The generic lake browser renders in place at #/Lake/... (not #/PSRS/),
			// reusing the 'LakeBrowse' recordset; match that route too.
			const tmpListMatch = tmpHash.match(/^#\/PSRS\/([^\/]+)\//);
			const tmpRouteRecordSet = tmpListMatch ? tmpListMatch[1] : (/^#\/Lake\//.test(tmpHash) ? 'LakeBrowse' : null);
			const tmpConfigurations = (this.pict.PictSectionRecordSet && this.pict.PictSectionRecordSet.recordSetProviderConfigurations) || {};
			const tmpPaintedConfig = this._recordSetBeingPainted ? tmpConfigurations[this._recordSetBeingPainted] : null;
			const tmpHideRowActions = !!(tmpPaintedConfig && tmpPaintedConfig.RecordSetListHideRowActions === true);
			if (!tmpRouteRecordSet && !tmpHideRowActions)
			{
				return;
			}
			// Only the app that owns the page blanks the column by route; an embedding app keeps its own lists' View links.
			const tmpProviderConfig = (tmpRouteRecordSet && this.options.AppChrome !== false) ? tmpConfigurations[tmpRouteRecordSet] : null;
			const tmpIsDoc = !!(tmpProviderConfig && tmpProviderConfig.RecordSetMeadowEntity === 'Document');
			// PrivateDataLake recordsets (the SiteManager dashboards + the generic
			// #/Lake browser) have no working Read view — their audit columns carry
			// the full physical name, so the stock View link resolves an empty GUID
			// and routes nowhere (see follow-up #30). Suppress the action column for
			// them too; navigation is via the custom drill/back links instead.
			const tmpIsLake = !!(tmpProviderConfig && /\/PrivateDataLake\//.test(tmpProviderConfig.URLPrefix || tmpProviderConfig.RecordSetURLPrefix || ''));
			if (!tmpIsDoc && !tmpIsLake && !tmpHideRowActions)
			{
				return;
			}
			if (this.pict.TemplateProvider && typeof this.pict.TemplateProvider.addTemplate === 'function')
			{
				this.pict.TemplateProvider.addTemplate('PRSP-List-RecordListAction-Template-Cell', '');
				this.pict.TemplateProvider.addTemplate('PRSP-List-RecordListActions-Template-Header', '');
				this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-RecordListAction-Template-Cell', '');
				this.pict.TemplateProvider.addTemplate('PRSP-Dashboard-RecordListActions-Template-Header', '');
			}
		}
		catch (pError)
		{
			this.log.warn(`DynamicDashboardLoader: failed to suppress default action column: ${pError.message || pError}`);
		}
	}

	/**
	 * Drop `IDDevice` from the EntityProvider's connected-entity cache list so
	 * the framework never tries to bulk-fetch Devices (or fall back to per-row
	 * Device lookups when /Devices/Count is missing on the meadow deployment).
	 *
	 * Implemented as a one-time wrap around `cacheConnectedEntityRecords` —
	 * filters `pIDListToCache` and the parallel `pEntityListToCache`, then
	 * delegates to the original. Any other connected-entity caching (User,
	 * Project, etc.) keeps working untouched.
	 */
	_skipDeviceCaching()
	{
		if (this._deviceCachingDisabled) return;
		this._deviceCachingDisabled = true;
		const tmpEP = this.pict && this.pict.EntityProvider;
		if (!tmpEP) return;
		// Drop, from connected-entity caching:
		//  - 'IDDevice' (no /Devices/Count on some deployments);
		//  - lake physical-name PKs (IDC<n>_<table>): those don't match ID<Entity>,
		//    so the framework reads the PK as a foreign key and bulk-fetches a
		//    non-existent entity → 404, which aborts the record bind (#30 / #35);
		//  - 'IDDocument': denormalized lake tables (e.g. element inspections) carry
		//    the source document id as a column. The framework reads it as an FK and
		//    bulk-fetches the DECORATED /1.0/Documents endpoint with an INN list of
		//    every distinct id — the per-row CanEdit fan-out makes that hang for tens
		//    of seconds on a large set, blocking the whole dashboard's record bind.
		//    The per-row IDDocument value still displays (and drives the doc-link
		//    action); only the pointless entity prefetch is skipped.
		const fShouldDrop = (pField) => pField === 'IDDevice' /*TODO: || pField === 'IDDocument' */ || /^IDC\d+_/.test(pField);
		// Both the with-count and without-count variants need wrapping — the list
		// path uses one, the chart/dashboard record load the other.
		const fWrapConnectedCache = (pMethodName) =>
		{
			if (typeof tmpEP[pMethodName] !== 'function') return;
			const tmpOriginal = tmpEP[pMethodName].bind(tmpEP);
			tmpEP[pMethodName] = function(pFirst, pIDList, pEntityList, pLiteRecords, fCallback)
			{
				const tmpIDs = Array.isArray(pIDList) ? pIDList : [];
				const tmpEntities = Array.isArray(pEntityList) ? pEntityList : [];
				const tmpFilteredIDs = [];
				const tmpFilteredEntities = [];
				for (let i = 0; i < tmpIDs.length; i++)
				{
					if (fShouldDrop(tmpIDs[i])) continue;
					tmpFilteredIDs.push(tmpIDs[i]);
					tmpFilteredEntities.push(tmpEntities[i]);
				}
				return tmpOriginal(pFirst, tmpFilteredIDs, tmpFilteredEntities, pLiteRecords, fCallback);
			};
		};
		fWrapConnectedCache('cacheConnectedEntityRecords');
		fWrapConnectedCache('cacheConnectedEntityRecordsWithoutCount');
	}

	/**
	 * Opt-in client-side filter mode for a dashboard.
	 *
	 * The data app's recordset filters are by default sent to meadow as FBV
	 * clauses on top-level columns. That works for genuine table columns but
	 * breaks for fields buried inside a JSON Datum blob (Mix Design, Material,
	 * BucketDate, etc.) — meadow can't reach into JSON without JSON_EXTRACT.
	 *
	 * Approach:
	 *   1. Override `_prepareFilterState` to STRIP Bundle-stored FilterClauses
	 *      before each server fetch. Server only ever sees the URL scope filter
	 *      (Type=AggregatedDashboardData & AppHash=…), so it always returns the
	 *      full dashboard cohort.
	 *   2. Wrap getRecords/getDecoratedRecords to apply the user's clauses
	 *      against the parsed records locally, supporting nested paths via
	 *      `fable.Utility.getValueByHash`.
	 *   3. Inject the dashboard's declared `ClientSide.Filters` entries into
	 *      `_FilterSchema` so they appear in the "Add Filters" picker.
	 *
	 * The opt-in keeps server-side filtering as the default for future Walbec
	 * dashboards that DO want to push down to the lake.
	 *
	 * @param {any} pProvider - The recordset provider returned from loadRecordSetConfiguration.
	 * @param {Record<string, any>} pManifest - The manifest (with optional ClientSide config).
	 * @param {{ Hash: string, RecordSet: string, Title: string }} pDashboardEntry
	 */
	_installClientSideFiltering(pProvider, pManifest, pDashboardEntry)
	{
		if (!pProvider || pProvider._clientSideFilteringInstalled)
		{
			return;
		}

		// (1) Strip user clauses before they reach the server. The Bundle still
		// holds them so the filter UI / chips render correctly; we just don't
		// forward them as FBV.
		if (typeof pProvider._prepareFilterState === 'function')
		{
			const tmpOriginal = pProvider._prepareFilterState.bind(pProvider);
			pProvider._prepareFilterState = (pEntity, pOptions, pAddr) =>
			{
				const tmpResult = tmpOriginal(pEntity, pOptions, pAddr);
				const tmpClauses = Array.isArray(tmpResult[0]) ? tmpResult[0] : [];
				// Keep only RawFilter entries (the URL scope) — drop everything
				// else; we apply the rest client-side after the response lands.
				const tmpScopeOnly = tmpClauses.filter((pC) => pC && pC.Type === 'RawFilter');
				return [ tmpScopeOnly, tmpResult[1] ];
			};
		}

		// (2) Wrap the data-fetch entry points so the user's FilterClauses get
		// applied client-side after the server returns scope-only records. The
		// existing _installDatumParser wrapper already chains through these
		// methods; we overlay the filter step on top via a second wrap.
		const tmpApplyClientFilters = (pRecords) =>
		{
			const tmpInputRecords = Array.isArray(pRecords) ? pRecords : [];
			const tmpClauses = (this.pict.Bundle && this.pict.Bundle._ActiveFilterState &&
				this.pict.Bundle._ActiveFilterState[pDashboardEntry.RecordSet] &&
				this.pict.Bundle._ActiveFilterState[pDashboardEntry.RecordSet].FilterClauses) || [];
			if (!Array.isArray(tmpClauses) || tmpClauses.length === 0)
			{
				return tmpInputRecords;
			}
			return this._applyClientFilterClauses(tmpInputRecords, tmpClauses);
		};

		const tmpRewireFetchMethod = (pMethodName) =>
		{
			const tmpOriginal = pProvider[pMethodName] && pProvider[pMethodName].bind(pProvider);
			if (!tmpOriginal) return;
			pProvider[pMethodName] = async (...pArgs) =>
			{
				const tmpResult = await tmpOriginal(...pArgs);
				if (tmpResult && Array.isArray(tmpResult.Records))
				{
					tmpResult.Records = tmpApplyClientFilters(tmpResult.Records);
					// Re-stash filtered records so KPIs / charts reflect the filter.
					try
					{
						this.pict.AppData.WalbecRecords = tmpResult.Records;
						this.pict.AppData.WalbecSummary = this._computeDashboardSummary(pManifest.Scope, tmpResult.Records);
					}
					catch (pError)
					{/* keep going on summary failure */}
				}
				return tmpResult;
			};
		};
		tmpRewireFetchMethod('getRecords');
		tmpRewireFetchMethod('getDecoratedRecords');
		tmpRewireFetchMethod('getRecordsInline');

		// Override count: server sees only scope so its count is the cohort
		// size; subtract clauses client-side and report the filtered length.
		if (typeof pProvider.getRecordSetCount === 'function')
		{
			const tmpOriginalCount = pProvider.getRecordSetCount.bind(pProvider);
			pProvider.getRecordSetCount = async (pOptions) =>
			{
				const tmpRecordsResult = await pProvider.getRecords(pOptions);
				const tmpFiltered = tmpRecordsResult && Array.isArray(tmpRecordsResult.Records) ?
					tmpRecordsResult.Records :
					[];
				// Best-effort: report total-after-filter as both count fields the
				// dashboard shell looks at. Falls back to the original on error.
				try
				{
					const tmpServerCount = await tmpOriginalCount(pOptions);
					return Object.assign({}, tmpServerCount, { Count: tmpFiltered.length });
				}
				catch (pError)
				{
					return { Count: tmpFiltered.length };
				}
			};
		}

		// (3) Inject manifest-declared filter columns into the picker schema.
		this._injectClientSideFilterSchema(pProvider, pManifest, pDashboardEntry);

		pProvider._clientSideFilteringInstalled = true;
	}

	/**
	 * Apply an array of filter clauses to a record list, returning the subset
	 * that matches all clauses. Reads field values via the manyfest hash
	 * resolver so clauses can target nested paths (e.g. `Datum.MixDesigns.0.MixDesign`).
	 *
	 * Supports the clause Types our dashboards use:
	 *   - StringMatch (ExactMatch true/false)
	 *   - DateRange / DateMatch
	 *   - NumericRange / NumericMatch
	 *   - StringRange
	 *
	 * @param {Array<Record<string, any>>} pRecords
	 * @param {Array<Record<string, any>>} pClauses
	 * @return {Array<Record<string, any>>}
	 */
	_applyClientFilterClauses(pRecords, pClauses)
	{
		if (!Array.isArray(pRecords) || !Array.isArray(pClauses)) return pRecords || [];
		const tmpUtility = this.pict && this.pict.fable && this.pict.fable.Utility;
		const tmpReadField = (pRecord, pColumn) =>
		{
			if (!pColumn) return undefined;
			if (tmpUtility && typeof tmpUtility.getValueByHash === 'function')
			{
				return tmpUtility.getValueByHash(pRecord, pColumn);
			}
			// Manual dotted-path fallback if the utility isn't reachable.
			let tmpRef = pRecord;
			for (const tmpKey of String(pColumn).split('.'))
			{
				if (tmpRef == null) return undefined;
				tmpRef = tmpRef[tmpKey];
			}
			return tmpRef;
		};
		const tmpToTime = (pVal) =>
		{
			if (pVal == null || pVal === '') return null;
			const tmpDate = new Date(pVal);
			return Number.isNaN(tmpDate.getTime()) ? null : tmpDate.getTime();
		};

		const tmpClauseMatches = (pRecord, pClause) =>
		{
			if (!pClause || !pClause.FilterByColumn) return true;
			const tmpField = tmpReadField(pRecord, pClause.FilterByColumn);
			const tmpType = String(pClause.Type || '');
			const tmpValues = pClause.Values || {};
			switch (tmpType)
			{
			case 'StringMatch':
			{
				const tmpQuery = tmpValues.value;
				if (tmpQuery === undefined || tmpQuery === null || tmpQuery === '') return true;
				const tmpStrField = tmpField == null ? '' : String(tmpField);
				const tmpStrQuery = String(tmpQuery);
				if (pClause.ExactMatch) return tmpStrField === tmpStrQuery;
				return tmpStrField.toLowerCase().includes(tmpStrQuery.toLowerCase());
			}
			case 'StringRange':
			{
				const tmpStart = tmpValues.Start;
				const tmpEnd = tmpValues.End;
				const tmpStrField = tmpField == null ? '' : String(tmpField);
				if (tmpStart != null && tmpStart !== '' && tmpStrField < String(tmpStart)) return false;
				if (tmpEnd != null && tmpEnd !== '' && tmpStrField > String(tmpEnd)) return false;
				return true;
			}
			case 'DateRange':
			{
				const tmpStart = tmpToTime(tmpValues.Start);
				const tmpEnd = tmpToTime(tmpValues.End);
				const tmpFieldT = tmpToTime(tmpField);
				if (tmpFieldT == null) return tmpStart == null && tmpEnd == null;
				if (tmpStart != null && tmpFieldT < tmpStart) return false;
				if (tmpEnd != null && tmpFieldT > tmpEnd) return false;
				return true;
			}
			case 'DateMatch':
			{
				const tmpExpect = tmpToTime(tmpValues.value);
				if (tmpExpect == null) return true;
				const tmpFieldT = tmpToTime(tmpField);
				if (tmpFieldT == null) return false;
				if (pClause.ExactMatch) return tmpFieldT === tmpExpect;
				// Same calendar day fuzzy comparison.
				return new Date(tmpFieldT).toISOString().slice(0, 10) === new Date(tmpExpect).toISOString().slice(0, 10);
			}
			case 'NumericRange':
			{
				const tmpStart = tmpValues.Start;
				const tmpEnd = tmpValues.End;
				const tmpNum = Number(tmpField);
				if (!Number.isFinite(tmpNum)) return tmpStart == null && tmpEnd == null;
				if (tmpStart != null && tmpStart !== '' && tmpNum < Number(tmpStart)) return false;
				if (tmpEnd != null && tmpEnd !== '' && tmpNum > Number(tmpEnd)) return false;
				return true;
			}
			case 'NumericMatch':
			{
				const tmpExpect = tmpValues.value;
				if (tmpExpect === undefined || tmpExpect === null || tmpExpect === '') return true;
				return Number(tmpField) === Number(tmpExpect);
			}
			default:
				// Unknown / unsupported types: keep the record (fail open) so
				// the UI doesn't silently drop everything when a future
				// clause type isn't recognized.
				return true;
			}
		};

		return pRecords.filter((pRecord) => pClauses.every((pClause) => tmpClauseMatches(pRecord, pClause)));
	}

	/**
	 * Inject the manifest's `ClientSide.Filters` entries into the recordset
	 * provider's `_FilterSchema` so they appear in the dashboard's "Add Filters"
	 * picker alongside (or in place of) the server-derived schema columns.
	 *
	 * Each entry in pManifest.ClientSide.Filters has:
	 *   - FilterByColumn: e.g. "Datum.MixDesigns.0.MixDesign"
	 *   - Type:           "StringMatch" | "DateRange" | "NumericRange" | …
	 *   - DisplayName:    label in the picker
	 *   - Clauses:        optional array of { ClauseKey, DisplayName, Type, ExactMatch } overrides
	 *
	 * @param {any} pProvider
	 * @param {Record<string, any>} pManifest
	 * @param {{ RecordSet: string }} pDashboardEntry
	 */
	_injectClientSideFilterSchema(pProvider, pManifest, pDashboardEntry)
	{
		const tmpFilters = (pManifest.ClientSide && pManifest.ClientSide.Filters) || [];
		if (!Array.isArray(tmpFilters) || tmpFilters.length === 0) return;
		if (!pProvider._FilterSchema || typeof pProvider._FilterSchema !== 'object') return;

		// Optionally clear the schema-driven filters so users don't see internal
		// columns like "App Data Unique Database ID". Driven by manifest flag.
		if (pManifest.ClientSide && pManifest.ClientSide.HideSchemaFilters)
		{
			for (const tmpKey of Object.keys(pProvider._FilterSchema))
			{
				delete pProvider._FilterSchema[tmpKey];
			}
		}

		let tmpOrdinal = 1000;
		for (const tmpFilter of tmpFilters)
		{
			if (!tmpFilter || !tmpFilter.FilterByColumn) continue;
			const tmpKey = tmpFilter.FilterKey || tmpFilter.FilterByColumn;
			const tmpDisplayName = tmpFilter.DisplayName || tmpFilter.Label || tmpKey;
			const tmpType = tmpFilter.Type || 'StringMatch';
			const tmpClauses = Array.isArray(tmpFilter.Clauses) && tmpFilter.Clauses.length > 0 ?
				tmpFilter.Clauses :
				this._defaultClausesForFilterType(tmpType, tmpKey, tmpDisplayName);

			pProvider._FilterSchema[tmpKey] =
				{
					FilterKey: tmpKey,
					RecordSet: pDashboardEntry.RecordSet,
					DisplayName: tmpDisplayName,
					Description: tmpFilter.Description || `Filter by ${tmpDisplayName}`,
					HelpText: tmpFilter.HelpText || `Filter records by ${tmpDisplayName}.`,
					Ordinal: ++tmpOrdinal,
					AvailableClauses: tmpClauses.map((pC, pIdx) => Object.assign(
						{
							FilterKey: tmpKey,
							ClauseKey: pC.ClauseKey || `${tmpKey}_${pIdx}`,
							FilterByColumn: tmpFilter.FilterByColumn,
							DisplayName: pC.DisplayName || pC.Label || tmpDisplayName,
							Label: pC.Label || `${tmpDisplayName} ${pC.DisplayName || ''}`.trim(),
							Type: pC.Type || tmpType,
							ExactMatch: pC.ExactMatch,
							Ordinal: pIdx + 1,
							MinimumLabel: pC.MinimumLabel,
							MaximumLabel: pC.MaximumLabel,
						},
						pC.Extra || {},
					)),
				};
		}
	}

	/**
	 * Default clause set for a given filter type, used when the manifest entry
	 * doesn't specify its own. Mirrors the meadow provider's auto-generated
	 * clause shape so the UI for Datum.* filters looks identical to top-level
	 * column filters.
	 *
	 * @param {string} pType
	 * @param {string} pKey
	 * @param {string} pDisplayName
	 * @return {Array<Record<string, any>>}
	 */
	_defaultClausesForFilterType(pType, pKey, pDisplayName)
	{
		switch (pType)
		{
		case 'StringMatch':
			return [
				{ ClauseKey: `${pKey}_Match_Exact`, DisplayName: 'Exact Match', Type: 'StringMatch', ExactMatch: true, Label: `${pDisplayName} Exact Match` },
				{ ClauseKey: `${pKey}_Match_Fuzzy`, DisplayName: 'Partial Match', Type: 'StringMatch', ExactMatch: false, Label: `${pDisplayName} Partial Match` },
			];
		case 'DateRange':
		case 'DateMatch':
			return [
				{ ClauseKey: `${pKey}_Match_Exact`, DisplayName: 'Exact Match', Type: 'DateMatch', ExactMatch: true, Label: `${pDisplayName} Exact Match` },
				{ ClauseKey: `${pKey}_Range`, DisplayName: 'In Range', Type: 'DateRange', Label: `${pDisplayName} In Range`,
					  MinimumLabel: `Minimum ${pDisplayName}`, MaximumLabel: `Maximum ${pDisplayName}` },
			];
		case 'NumericRange':
		case 'NumericMatch':
			return [
				{ ClauseKey: `${pKey}_Match_Exact`, DisplayName: 'Exact Match', Type: 'NumericMatch', ExactMatch: true, Label: `${pDisplayName} Exact Match` },
				{ ClauseKey: `${pKey}_Range`, DisplayName: 'In Range', Type: 'NumericRange', Label: `${pDisplayName} In Range`,
					  MinimumLabel: `Minimum ${pDisplayName}`, MaximumLabel: `Maximum ${pDisplayName}` },
			];
		default:
			return [
				{ ClauseKey: `${pKey}_Match`, DisplayName: 'Match', Type: pType, Label: `${pDisplayName} Match` },
			];
		}
	}

	/**
	 * Run a lake dashboard's manifest GlobalSolvers (the config-declared aggregation)
	 * against the fetched rows, writing WalbecDaily + WalbecSummary to pict.AppData.
	 * Generic — the per-dashboard logic lives in the manifest, not here. Re-runs on
	 * every load/filter (called from _computeDashboardSummary), so filters re-aggregate.
	 *
	 * @param {Record<string, any>} pManifest
	 * @param {Array<Record<string, any>>} pRecords
	 * @return {boolean} Whether solvers ran.
	 */
	_runAggregationSolvers(pManifest, pRecords)
	{
		if (!pManifest || !Array.isArray(pManifest.GlobalSolvers) || pManifest.GlobalSolvers.length < 1)
		{
			return false;
		}
		const tmpParser = this.pict.ExpressionParser || this.pict.instantiateServiceProviderIfNotExists('ExpressionParser');
		// solve() resolves nested addresses (AppData._x) through the manifest's getValueByHash,
		// so the manifest arg MUST be a real Manyfest, not null. Reuse pict's shared instance.
		const tmpManyfest = this.pict.manifest;
		if (!tmpManyfest)
		{
			return false;
		}
		if (!this.pict.AppData.WalbecDaily)
		{
			this.pict.AppData.WalbecDaily = {};
		}
		if (!this.pict.AppData.WalbecSummary)
		{
			this.pict.AppData.WalbecSummary = {};
		}
		const tmpContext = { Pict: this.pict, AppData: this.pict.AppData, Bundle: this.pict.Bundle, RecordSubset: Array.isArray(pRecords) ? pRecords : [], Manifest: tmpManyfest };
		for (const tmpExpression of pManifest.GlobalSolvers)
		{
			try
			{
				tmpParser.solve(tmpExpression, tmpContext, {}, tmpManyfest, tmpContext);
			}
			catch (pError)
			{
				this.log.warn(`DynamicDashboardLoader: aggregation solver failed [${tmpExpression}]: ${pError.message}`);
			}
		}
		// The GlobalSolvers set each day's IDDocumentCount to a per-bucket SUM, which over-counts a
		// document shared across product/result buckets (one worksheet covering 20 stockpile rows
		// reads as "20"). Recompute it as the DISTINCT document count per day from the lake rows'
		// IDDocuments CSV, so the "N docs" button matches the documents popup. (The solver DSL can't
		// dedup CSV strings, so this lives here in JS.)
		this._applyDistinctDocumentCounts(Array.isArray(pRecords) ? pRecords : [], this.pict.AppData.WalbecDailyRecords);
		return true;
	}

	/**
	 * Overwrite each day-row's IDDocumentCount with the count of DISTINCT documents contributed
	 * that day (deduped across the day's lake rows' comma-separated IDDocuments lists), so the
	 * Documents button count matches the documents popup.
	 *
	 * @param {Array<Record<string, any>>} pLakeRows - Lake rows (each with BucketDate + IDDocuments).
	 * @param {Array<Record<string, any>>} pDailyRecords - The per-day rows the detail table renders.
	 */
	_applyDistinctDocumentCounts(pLakeRows, pDailyRecords)
	{
		if (!Array.isArray(pLakeRows) || !Array.isArray(pDailyRecords))
		{
			return;
		}
		const tmpDistinctByDate = {};
		for (const tmpRow of pLakeRows)
		{
			// Rollup-grain tables carry IDDocuments (CSV); doc-grain tables
			// (e.g. HMA running-avg) carry a single IDDocument per row.
			const tmpDocumentIDs = (tmpRow && tmpRow.IDDocuments != null) ? tmpRow.IDDocuments :
				(tmpRow ? tmpRow.IDDocument : null);
			if (tmpDocumentIDs == null)
			{
				continue;
			}
			const tmpKey = String(tmpRow.BucketDate);
			const tmpSet = tmpDistinctByDate[tmpKey] || (tmpDistinctByDate[tmpKey] = {});
			for (const tmpPart of String(tmpDocumentIDs).split(','))
			{
				const tmpID = parseInt(String(tmpPart).trim(), 10);
				if (Number.isInteger(tmpID) && tmpID > 0)
				{
					tmpSet[tmpID] = true;
				}
			}
		}
		for (const tmpDay of pDailyRecords)
		{
			if (!tmpDay)
			{
				continue;
			}
			// Day rows built by GENERATEARRAYOFOBJECTSFROMSETS carry only
			// BucketLabel; others carry BucketDate. Same calendar-day string
			// either way.
			const tmpSet = tmpDistinctByDate[String((tmpDay.BucketDate != null) ? tmpDay.BucketDate : tmpDay.BucketLabel)];
			if (!tmpSet)
			{
				// No per-document evidence for this day — keep the manifest
				// solver's count rather than stomping a working value to zero.
				continue;
			}
			tmpDay.IDDocumentCount = Object.keys(tmpSet).length;
		}
	}

	/**
	 * Compute a dashboard-specific summary object across all currently-loaded
	 * bucket records. KPI ReadOnly inputs and chart solvers in the form-mode
	 * manifest read fields off this object.
	 *
	 * Each branch returns a flat object whose top-level keys match the
	 * DataAddress paths in that dashboard's manifest descriptors.
	 *
	 * @param {string} pHash - Dashboard hash (manifest Scope).
	 * @param {Array<Record<string, any>>} pRecords - Bucket records (Datum already parsed).
	 * @return {Record<string, any>}
	 */
	_computeDashboardSummary(pHash, pRecords)
	{
		const tmpRecords = Array.isArray(pRecords) ? pRecords : [];
		// Lake-sourced dashboards return flat rows (no Datum blob) — drive them
		// entirely from the manifest's GlobalSolvers. This covers both daily/
		// bucketed dashboards (walbec) AND inventory-style ones with no BucketDate
		// (e.g. HDOT bridge inventory); the earlier BucketDate gate silently
		// skipped the latter, leaving RecordSubset unset → blank KPIs/charts (#35).
		const tmpLakeManifest = this._dashboardManifests && this._dashboardManifests[pHash];
		// An EMPTY result set MUST still run the solvers. This used to read
		// `tmpRecords[0] && tmpRecords[0].Datum == null` -- sniffing the first record to
		// decide "is this a flat lake dashboard?" -- which is unanswerable when there is
		// no first record. So a filter that matched nothing fell straight through, the
		// solvers never ran, and every KPI kept the PREVIOUS filter's number while the
		// header honestly reported "0 of 0 total records". Observed on QA: 0 records
		// shown beside tiles still reading 124 samples / 34,853.0 tons / 71 days, which
		// a reader believes over the grey text.
		//
		// The manifest is what actually identifies a lake-sourced dashboard, and it is
		// known without inspecting any record. Solving an empty subset is cheap and
		// yields the zeroes the tiles should show.
		if (tmpLakeManifest && (tmpRecords.length < 1 || tmpRecords[0].Datum == null))
		{
			this._runAggregationSolvers(tmpLakeManifest, tmpRecords);
			return (this.pict.AppData && this.pict.AppData.WalbecSummary) || {};
		}
		const tmpData = tmpRecords.map((pR) => pR && pR.Datum).filter((pD) => pD && typeof pD === 'object');
		const tmpSum = (pField) => tmpData.reduce((pAcc, pD) => pAcc + (Number(pD[pField]) || 0), 0);
		const tmpAvg = (pField) =>
		{
			const tmpVals = tmpData.map((pD) => Number(pD[pField])).filter((pN) => Number.isFinite(pN));
			return tmpVals.length ? tmpVals.reduce((pA, pB) => pA + pB, 0) / tmpVals.length : 0;
		};
		const tmpRound = (pN, pD) => Math.round(pN * Math.pow(10, pD)) / Math.pow(10, pD);

		switch (pHash)
		{
		case 'walbec-moisture-daily':
			return {
				BucketCount: tmpData.length,
				TotalSamples: tmpSum('TotalSamples'),
				AvgMoisture: tmpRound(tmpAvg('OverallAvgMoisture'), 2),
				AvgMeter: tmpRound(tmpAvg('AvgMeter'), 2),
				AvgOven: tmpRound(tmpAvg('AvgOven'), 2),
				AvgDifference: tmpRound(tmpAvg('AvgDifference'), 2),
				AvgPassRate: tmpRound(tmpAvg('OverallPassRate') * 100, 1),
				DateFrom: tmpData.length ? tmpData[0].BucketDate : '',
				DateTo: tmpData.length ? tmpData[tmpData.length - 1].BucketDate : '',
			};
		case 'walbec-hma-running-avg':
		{
			// Aggregate across each bucket's MixDesigns[] entries — these
			// are the per-mix per-day rollups; weight by SampleCount where
			// each mix had measurements.
			let tmpMixSamples = 0; let tmpAirNum = 0; let tmpAirDen = 0; let tmpBinderNum = 0; let tmpBinderDen = 0; let tmpVMANum = 0; let tmpVMADen = 0;
			const tmpUniqueMixes = new Set();
			for (const tmpDatum of tmpData)
			{
				if (!Array.isArray(tmpDatum.MixDesigns)) continue;
				for (const tmpMD of tmpDatum.MixDesigns)
				{
					const tmpCount = Number(tmpMD.SampleCount) || 0;
					if (tmpMD.MixDesign) tmpUniqueMixes.add(String(tmpMD.MixDesign));
					tmpMixSamples += tmpCount;
					if (Number.isFinite(Number(tmpMD.AirVoidsAvg)))
					{
						tmpAirNum += Number(tmpMD.AirVoidsAvg) * tmpCount; tmpAirDen += tmpCount;
					}
					if (Number.isFinite(Number(tmpMD.BinderPctAvg)))
					{
						tmpBinderNum += Number(tmpMD.BinderPctAvg) * tmpCount; tmpBinderDen += tmpCount;
					}
					if (Number.isFinite(Number(tmpMD.VMAAvg)))
					{
						tmpVMANum += Number(tmpMD.VMAAvg) * tmpCount; tmpVMADen += tmpCount;
					}
				}
			}
			return {
				BucketCount: tmpData.length,
				TotalTonnage: tmpRound(tmpSum('DailyTonnage'), 0),
				AvgDailyTonnage: tmpRound(tmpAvg('DailyTonnage'), 1),
				AvgAirVoids: tmpAirDen ? tmpRound(tmpAirNum / tmpAirDen, 2) : 0,
				AvgBinderPct: tmpBinderDen ? tmpRound(tmpBinderNum / tmpBinderDen, 2) : 0,
				AvgVMA: tmpVMADen ? tmpRound(tmpVMANum / tmpVMADen, 2) : 0,
				MixDesignCount: tmpUniqueMixes.size,
				TotalSamples: tmpMixSamples,
				DateFrom: tmpData.length ? tmpData[0].BucketDate : '',
				DateTo: tmpData.length ? tmpData[tmpData.length - 1].BucketDate : '',
			};
		}
		case 'walbec-cpsgrad-stats':
		{
			let tmpMatSamples = 0; let tmpMoistNum = 0; let tmpMoistDen = 0;
			const tmpUniqueMaterials = new Set();
			for (const tmpDatum of tmpData)
			{
				if (!Array.isArray(tmpDatum.Materials)) continue;
				for (const tmpMat of tmpDatum.Materials)
				{
					const tmpCount = Number(tmpMat.SampleCount) || 0;
					if (tmpMat.Material) tmpUniqueMaterials.add(String(tmpMat.Material));
					tmpMatSamples += tmpCount;
					if (Number.isFinite(Number(tmpMat.MoistureAvg)))
					{
						tmpMoistNum += Number(tmpMat.MoistureAvg) * tmpCount; tmpMoistDen += tmpCount;
					}
				}
			}
			return {
				BucketCount: tmpData.length,
				MaterialCount: tmpUniqueMaterials.size,
				TotalSamples: tmpMatSamples,
				AvgMoisture: tmpMoistDen ? tmpRound(tmpMoistNum / tmpMoistDen, 2) : 0,
				DateFrom: tmpData.length ? tmpData[0].BucketDate : '',
				DateTo: tmpData.length ? tmpData[tmpData.length - 1].BucketDate : '',
			};
		}
		default:
			return { BucketCount: tmpData.length };
		}
	}

	/**
	 * Top-level orchestration: for the given customer, load the AvailableDashboards
	 * record, fetch each referenced ManifestSchema, register them all, and stash
	 * the resolved list onto pict.AppData.AvailableDashboards for the chooser view.
	 *
	 * Safe no-op for customers with no AvailableDashboards record (returns null).
	 *
	 * @param {number} pCustomerID - The IDCustomer to wire dashboards for.
	 * @param {{ Enforced?: boolean, Dashboards?: Array<{ Hash: string, ProjectBound?: boolean, DataFilterString?: string }> }|null} [pAccess] -
	 *   The PlatformDashboard/Available answer. When it enforces access, only granted dashboards are wired, each with its
	 *   role filter; project-bound ones are left to hosts that open them for a project.
	 * @return {Promise<{ Dashboards: Array } | null>} The loaded dashboard list, or null.
	 */
	async wireAvailableDashboardsForCustomer(pCustomerID, pAccess)
	{
		// NOTE: the CSS injection, bottom-toolbar mount, and render-hook installs
		// below run for EVERY logged-in customer — even those with no dashboards —
		// so the persistent bottom bar (and its always-present Logout) is available
		// app-wide. Only the dashboard registration loop is gated on tmpAvailable.
		const tmpAvailable = await this.loadAvailableDashboards(pCustomerID);

		this.installDashboardStyles();
		try
		{
			if (this.pict.CSSMap && typeof this.pict.CSSMap.addCSS === 'function')
			{
				this.pict.CSSMap.addCSS('DynamicDashboardLoader-CSS', /*css*/`
					/* ---------------------------------------------------------------
					   PRINT / SUBMITTAL VIEW
					   ---------------------------------------------------------------
					   Registered through CSSMap rather than css/data.css on purpose:
					   that file is linked media="screen", so @media print rules in it
					   never apply. The CSSMap <style> is the LAST stylesheet in <head>,
					   so these also win on source order.

					   The approach is visibility isolation (the pattern already proven
					   by SamplePlanDashboard): hide everything, re-show the dashboard
					   container, and pull it to the top of the page. That beats hiding
					   chrome element by element, which silently breaks the day someone
					   adds a new wrapper.
					*/
					.walbec-printhead { display: none; }

					@media print
					{
						/* Landscape: these are wide control charts and 20+ column
						   gradation tables. Portrait clips both. */
						@page { size: landscape; margin: 12mm 10mm 14mm 10mm; }

						body * { visibility: hidden !important; }
						#PRSP_Container, #PRSP_Container * { visibility: visible !important; }
						#PRSP_Container { position: absolute; left: 0; top: 0; width: 100%; }
						body { padding-bottom: 0 !important; background: #fff !important; }

						/* Interactive chrome has no meaning on paper. The filter bar is
						   hidden because its CONTENT is restated in the print header --
						   a reader needs the filter values, not the controls. */
						#PRSP_Filters_Container,
						#PRSP_FilterBar,
						#walbec-bottom-toolbar,
						#HeadlightLogin-Standard-Wrap,
						#HeadlightPanels-Container,
						#Headlight-Dynamic-Header-Container,
						.pagination,
						.prsp-pagination,
						#PRSP_Title_Container,
						.hl-view-description,
						.walbec-noprint
						{
							display: none !important;
						}

						/* The submittal header: what this report covers. */
						.walbec-printhead
						{
							display: block !important;
							visibility: visible !important;
							margin: 0 0 0.6rem;
							padding: 0 0 0.4rem;
							border-bottom: 2px solid #000;
							color: #000;
						}
						.walbec-printhead-title { font-size: 1.15rem; font-weight: 700; }
						.walbec-printhead-filters { font-size: 0.8rem; margin-top: 0.15rem; }
						/* Said out loud, because an unfiltered submittal is a different
						   document from a filtered one and must not look identical. */
						.walbec-printhead-unfiltered { font-style: italic; }
						.walbec-printhead-stamp { font-size: 0.7rem; margin-top: 0.1rem; color: #333; }

						/* The container cap would waste the page width too. */
						[id^="PSRS-Dashboard-"] .pict-form-section.container
						{
							max-width: none !important;
							width: 100% !important;
							margin-left: 0 !important;
							margin-right: 0 !important;
						}
						body > section.section { padding: 0 !important; }

						/* KPI tiles read as a strip of numbers, not input boxes. */
						[id^="PSRS-Dashboard-"] input.input
						{
							border: none !important;
							box-shadow: none !important;
							background: transparent !important;
							padding-left: 0 !important;
							color: #000 !important;
						}

						/* A chart split across a page break is unreadable. */
						[id^="PSRS-Dashboard-"] .pict-form-group,
						.pict-input-chart
						{
							break-inside: avoid;
							page-break-inside: avoid;
						}

						/* Tables: repeat the header on every page, never split a row.
						   Without the repeated header, page 2 onward is a wall of
						   unlabelled numbers. */
						[id^="PSRS-Dashboard-"] .section-table-wrapper { overflow: visible !important; }
						[id^="PSRS-Dashboard-"] .table-container { overflow: visible !important; }
						[id^="PSRS-Dashboard-"] table thead { display: table-header-group; }
						[id^="PSRS-Dashboard-"] table tr { break-inside: avoid; page-break-inside: avoid; }
						[id^="PSRS-Dashboard-"] .section-table-wrapper td,
						[id^="PSRS-Dashboard-"] .section-table-wrapper th
						{
							font-size: 0.62rem;
							padding: 2px 4px !important;
						}
					}

					/* The page gutter too: 1.5rem each side is plenty on a data screen,
					   and :has() means this only tightens the section that actually holds
					   a dashboard. A browser without :has() simply keeps the wider
					   default -- the container fix above is the one that matters. */
					body > section.section:has(#PRSP_Container)
					{
						padding-left: 1.5rem;
						padding-right: 1.5rem;
						padding-top: 1.5rem;
					}
					/* Single-page pagination — pure CSS via :has(). Hides the page-
					   button UL when it contains only one (or zero) <li>, which
					   is the "Showing 1 to N of N where N <= PageSize" case. */
					nav.pagination ul:has(> li:only-child),
					nav[aria-label="pagination"] ul:has(> li:only-child),
					nav.pagination ul:not(:has(> li + li)),
					nav[aria-label="pagination"] ul:not(:has(> li + li))
					{
						display: none !important;
					}
					/* Tight bottom toolbar — Back/breadcrumb on the left, exports right.
					   Toolbar is mounted into document.body and pinned with
					   position:fixed; pad the body bottom so the toolbar doesn't
					   cover the last row of the table. */
					body { padding-bottom: 4.25rem; }
					.walbec-bottom-toolbar
					{
						position: fixed; left: 0; right: 0; bottom: 0;
						display: flex; align-items: center; justify-content: space-between;
						padding: 0.4rem 1rem;
						background: rgba(255, 255, 255, 0.96);
						border-top: 1px solid #e0e4ec;
						box-shadow: 0 -2px 8px rgba(15, 23, 42, 0.06);
						z-index: 30;
						font-size: 0.85rem;
						backdrop-filter: blur(4px);
					}
					.walbec-bottom-toolbar-left  { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
					.walbec-bottom-toolbar-right { display: flex; align-items: center; gap: 0.4rem; }
					.walbec-bottom-toolbar .button { font-size: 0.8rem; }
					.walbec-toolbar-divider { display: inline-block; width: 1px; height: 20px; background: #cbd5e1; margin: 0 0.35rem; }
					.walbec-breadcrumb { margin: 0; font-size: 0.78rem; }
					.walbec-breadcrumb ul { margin: 0; }
					.walbec-breadcrumb a { cursor: pointer; }
					.walbec-breadcrumb li.is-active a { color: #4a5568; pointer-events: none; }
					/* Hide the framework's redundant top-of-page "View" link column for
					   our Document recordsets — once we rewrite the cell to hold our
					   buttons it has the right look already. */
				`, 600);
				if (typeof this.pict.CSSMap.injectCSS === 'function')
				{
					this.pict.CSSMap.injectCSS();
				}
			}
		}
		catch (pError)
		{
			this.log.warn(`DynamicDashboardLoader: failed to inject dashboard CSS: ${pError.message || pError}`);
		}

		// Mount the persistent bottom toolbar (Back / breadcrumb / CSV / Excel /
		// Logout) once into document.body. Updates flow through PictRouter's
		// `after` hook on every route change — no MutationObserver.
		if (this.options.AppChrome !== false)
		{
			this._mountBottomToolbar();
		}

		// Skip caching connected Device records. The /Devices/Count endpoint
		// isn't exposed on every meadow deployment; when missing, the
		// framework falls back to per-row entity decoration, which fetches
		// every single related record serially and stalls the list view.
		if (this.options.AppChrome !== false)
		{
			this._skipDeviceCaching();
		}

		// Suppress the framework's default Actions column on our Document
		// recordsets — we already render Open/PDF via RecordSetListExtraColumn —
		// and on any dashboard that sets HideRowActions. Hooks into PRSP-List-RecordList.onBeforeRender (same pattern as
		// ImprovedTabularRecordSetProvider) so the override is route-aware
		// without needing a MutationObserver.
		this._installActionColumnSuppressor();

		if (!tmpAvailable)
		{
			// Finished, with nothing to show -- a real empty state, not a pending one.
			this.pict.AppData.AvailableDashboards = { Dashboards: [], Loaded: true };
			this.log.info(`DynamicDashboardLoader: no AvailableDashboards record for customer ${pCustomerID}; skipping dynamic dashboard wiring.`);
			return null;
		}

		const tmpRegistered = [];
		const tmpGranted = (pAccess && pAccess.Enforced && Array.isArray(pAccess.Dashboards)) ? new Map(pAccess.Dashboards.map((pGrant) => [ pGrant.Hash, pGrant ])) : null;
		for (const tmpEntry of tmpAvailable.Dashboards)
		{
			const tmpGrant = tmpGranted ? tmpGranted.get(tmpEntry.Hash) : null;
			if (tmpGranted && (!tmpGrant || tmpGrant.ProjectBound))
			{
				continue;
			}
			const tmpHost = (tmpGrant && tmpGrant.DataFilterString) ? { ExtraFilter: tmpGrant.DataFilterString } : undefined;
			try
			{
				// List-mode entries skip the ManifestSchema fetch — they're plain
				// server-side recordset list views (e.g. "All Documents") that the
				// loader registers directly from the AvailableDashboards Datum.
				if (tmpEntry.Mode === 'List')
				{
					if (this.registerListDashboard(tmpEntry, pCustomerID, tmpHost))
					{
						tmpRegistered.push(tmpEntry);
					}
					continue;
				}

				const tmpManifest = await this.loadDashboardManifest(tmpEntry.Hash, pCustomerID);
				if (!tmpManifest)
				{
					this.log.warn(`DynamicDashboardLoader: dashboard ${tmpEntry.Hash} has no manifest; skipping.`);
					continue;
				}
				if (this.registerDashboard(tmpEntry, tmpManifest, pCustomerID, tmpHost))
				{
					tmpRegistered.push(tmpEntry);
				}
			}
			catch (pError)
			{
				this.log.error(`DynamicDashboardLoader: failed to load dashboard ${tmpEntry.Hash}: ${pError.message || pError}`, { Stack: pError.stack });
			}
		}

		// `Loaded` separates "finished, and there are none" from "still working".
		// Without it the chooser cannot tell the two apart, and renders its empty
		// state -- "No dashboards are configured for your account yet." -- during
		// the fetch, which flashes a false negative at every user on every load.
		const tmpResolved = { Dashboards: tmpRegistered, Loaded: true };
		this.pict.AppData.AvailableDashboards = tmpResolved;
		this.log.info(`DynamicDashboardLoader: wired ${tmpRegistered.length}/${tmpAvailable.Dashboards.length} dashboards for customer ${pCustomerID}.`);
		return tmpResolved;
	}
}

module.exports = DynamicDashboardLoader;
module.exports.default_configuration = _DEFAULT_CONFIGURATION;
